import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, ChevronRight } from 'lucide-react';
import { SingleSelectDropdown } from './ui/SingleSelectDropdown';
import { MultiSelectDropdown } from './ui/MultiSelectDropdown';
import { saveSurveyResponse } from '../lib/supabase';
const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycby-1LeeVhQnsQ-HQUY6j0uXLZYF0O3mbUiOXrKTeXtovNct2y-uxK-epAqrF-4Euu4uiA/exec';
interface SurveyData {
  // Demographics & Context (1-16)
  email: string;
  yourName: string;
  cofounderNames: string;
  age: string;
  gender: string;
  education: string;
  functionalBackground: string;
  numberOfCofounders: string;
  title: string;
  companyStage: string;
  companyAge: string;
  timeKnownCofounder: string;
  howMetCofounder: string;
  workedTogetherBefore: string;
  firstTimeCofounder: string;
  // Psychological Safety (17-23)
  mistakeHeldAgainst: number;
  bringUpProblems: number;
  rejectsOthersForDifferent: number;
  safeToTakeRisk: number;
  difficultToAskHelp: number;
  wouldNotUndermine: number;
  skillsValued: number;
  // Trust - Ability (24-29)
  cofounderCapable: number;
  cofounderSuccessful: number;
  cofounderKnowledgeable: number;
  confidentInSkills: number;
  specializedCapabilities: number;
  notWellQualified: number;
  // Trust - Benevolence (30-34)
  concernedAboutWelfare: number;
  needsImportant: number;
  wouldNotHurt: number;
  looksOutForMe: number;
  willHelpMe: number;
  // Trust - Integrity (35-40)
  senseOfJustice: number;
  sticksToWord: number;
  fairInDealings: number;
  actionsNotConsistent: number;
  likeValues: number;
  soundPrinciples: number;
  // Trust - Propensity (41-42)
  peopleDoWhatTheySay: number;
  mustBeAlert: number;
  // Investment Model - Satisfaction (43-47)
  satisfiedWithPartnership: number;
  betterThanOthers: number;
  closeToIdeal: number;
  makesHappy: number;
  fulfillsNeeds: number;
  // Investment Model - Alternatives (48-51)
  alternativesAttractive: number;
  needsEasilyFulfilled: number;
  couldFindAnother: number;
  wouldDoFine: number;
  // Investment Model - Investment (52-55)
  putGreatDeal: number;
  lifeLinked: number;
  veryInvolved: number;
  investedGreatDeal: number;
  // Investment Model - Commitment (56-60)
  wantToLastLong: number;
  committedToMaintaining: number;
  notUpsetIfEnds: number;
  workingIn3Years: number;
  stronglyAttached: number;
  attentionCheck1: number;
  // Conflict Style - Integrating (62-65)
  investigateIssue: number;
  integrateIdeas: number;
  workTogetherSolutions: number;
  exchangeInformation: number;
  // Conflict Style - Obliging (66-69)
  satisfyNeeds: number;
  accommodateWishes: number;
  giveInToWishes: number;
  goAlongWithSuggestions: number;
  // Conflict Style - Dominating (70-73)
  useInfluence: number;
  useAuthority: number;
  firmInPursuing: number;
  usePower: number;
  // Conflict Style - Avoiding (74-77)
  avoidBeingPutOnSpot: number;
  avoidOpenDiscussion: number;
  stayAwayFromDisagreement: number;
  avoidUnpleasantExchanges: number;
  // Conflict Style - Compromising (78-81)
  findMiddleCourse: number;
  proposeMiddleGround: number;
  negotiateCompromise: number;
  findFairCombination: number;
  // Communication - Frequency (82-83)
  communicateWorkFrequently: number;
  communicateNonWorkFrequently: number;
  // Communication - Timeliness (84-85)
  communicatesTimely: number;
  infoReachesLate: number;
  // Communication - Accuracy (86-87)
  communicatesAccurately: number;
  conflictingInfo: number;
  // Communication - Problem Solving (88-89)
  blamesOthers: number;
  workTogetherSolve: number;
  // Communication - Shared Knowledge (90-91)
  sharedGoals: number;
  understandConnection: number;
  // Communication - Mutual Respect (92-93)
  respectWork: number;
  contributionsValued: number;
  // Attachment - Anxious (94-99)
  worryNotValued: number;
  worryWantToEnd: number;
  worryALot: number;
  worryInterestedInOthers: number;
  desireScares: number;
  needReassurance: number;
  // Attachment - Avoidant (100-105)
  preferNotShowFeelings: number;
  comfortableSharingThoughts: number;
  difficultToDepend: number;
  nervousWhenClose: number;
  preferNotEmotionallyConnected: number;
  uncomfortableDiscussingFeelings: number;
  // Personality - TIPI (106-115)
  extravertedEnthusiastic: number;
  criticalQuarrelsome: number;
  dependableSelfDisciplined: number;
  anxiousEasilyUpset: number;
  openToExperiences: number;
  reservedQuiet: number;
  sympatheticWarm: number;
  disorganizedCareless: number;
  calmEmotionallyStable: number;
  conventionalUncreative: number;
  // Vision Alignment (117-120)
  commonVision: number;
  discussedLongTerm: number;
  differentDefinitions: number;
  guessTop3Priorities: number;
  // Role Clarity (121-124)
  rolesClearlyDefined: number;
  confusionAboutResponsibility: number;
  clearDecisionRights: number;
  stepOnToes: number;
  // Decision-Making (125-130)
  majorDecisions: string;
  disagreementResolution: string;
  tieBreaker: string;
  inputConsidered: number;
  decisionsWithoutInput: number;
  stuckOnDecisions: number;
  // Power Dynamics (131-136)
  seniorFounder: string;
  moreInfluence: string;
  equalWeight: number;
  cofounderDominates: number;
  holdBackOpinions: number;
  treatedAsEquals: number;
  // Equity - Structure (137-140)
  equitySplit: string;
  equitySplitDecision: string;
  vestingSchedules: string;
  buybackClause: string;
  // Equity - Fairness (141-148)
  equitySplitFair: number;
  stillFairToday: number;
  equityResentment: number;
  equityConversationOpen: number;
  comfortableRaisingEquityConcerns: number;
  advocateForInterests: number;
  trustEquityConversation: number;
  unresolvedEquityFeelings: number;
  // Equity - Compensation (149-152)
  salariesFair: number;
  compensationAligned: number;
  financialSacrificesFair: number;
  discussedFutureCompensation: number;
  // Equity - Discussions (153-157)
  explicitEquityTalk: string;
  equityConversationDifficulty: string;
  renegotiatedEquity: string;
  equityTension: string;
  equityTensionResolved: string;
  // Equity - Red Flags (158-161)
  wonderingFairShare: number;
  thoughtAboutEquityIfBad: number;
  worryEquityDamageRelationship: number;
  believeFairRenegotiation: number;
  // Financial & Risk (162-167)
  similarFinancialSituations: string;
  similarRiskTolerance: number;
  financialRunwayAffected: number;
  alignedOnPayment: number;
  financialStressCreatedTension: number;
  understandCofounderConstraints: number;
  // Recognition & Credit (168-173)
  creditSharedFairly: number;
  cofounderGetsMoreRecognition: number;
  cofounderCreditsMe: number;
  iCreditCofounder: number;
  externalRecognitionTension: number;
  discussedPublicRepresentation: string;
  // External Stakeholders (174-179)
  investorsAffectedRelationship: string;
  unitedFrontToInvestors: number;
  disagreedInFrontOfInvestors: number;
  employeePerceptionAffects: number;
  alignedOnTeamCommunication: number;
  advisorsHelpedRelationship: string;
  // Stress & Burnout (180-187)
  currentStressLevel: number;
  burnoutLevel: number;
  iWithdrawWhenStressed: number;
  cofounderWithdrawsWhenStressed: number;
  handleStressCompatibly: number;
  stressBroughtCloser: number;
  checkInOnWellbeing: number;
  comfortableSharingStruggles: number;
  // Work-Life Balance (188-193)
  similarLifeStages: string;
  similarWorkExpectations: number;
  cofounderRespectsMyBoundaries: number;
  iRespectCofounderBoundaries: number;
  lifeDifferencesCreatedTension: number;
  discussedLifeChanges: string;
  // Working Styles (194-199)
  howWorkTogether: string;
  similarPace: number;
  detailOrientedDifference: string;
  compatibleSchedules: number;
  compatibleCommunication: number;
  conversationFrequency: string;
  // Crisis Response (200-204)
  facedMajorCrisis: string;
  howHandledCrisis: string;
  crisisesStrengthened: number;
  trustCofounderInCrisis: number;
  hardestMomentShowUp: string;
  // Values & Ethics (205-208)
  similarEthicalStandards: number;
  discussedEthicalDilemmas: string;
  trustEthicalDecisions: number;
  alignedOnTreatment: number;
  // Future Planning (209-213)
  discussedCannotContinue: string;
  discussedStepBack: string;
  confidentHandleMajorChange: number;
  discussedSuccessionPlanning: string;
  discussedIfCompanyFails: string;
  // Commitment Alignment (214-216)
  similarExpectations: number;
  notAsCommitted: number;
  okayCarryingBusiness: number;
  // Resilience & Recovery (217-219)
  bouncedBackWell: number;
  wayOfReconnecting: number;
  predictReaction: number;
  // Energy & Regeneration (220-222)
  workingEnergizes: number;
  feelMoreClear: number;
  feelFrustratedDepleted: number;
  // Social Integration (223-225)
  spendTimeSocially: number;
  metFriendsFamily: string;
  honestConversation: string;
  // 3+ Cofounders (226-231)
  alliancesForm: string;
  clearLeader: string;
  decisionMakingComplicated: number;
  moreSupport: number;
  communicationHarder: number;
  sameNumberOfCofounders: string;
  // Behavioral Indicators (232-238)
  relationshipCheckIns: string;
  gaveConstructiveFeedback: string;
  receivedConstructiveFeedback: string;
  significantDisagreement: string;
  howResolved: string;
  formalAgreement: string;
  discussedLeaving: string;
  // Outcome Ratings (239-244)
  relationshipStrength: number;
  likelyWorkingIn3Years: number;
  cofounderSameAnswer: number;
  satisfactionOverall: number;
  survivesCrisis: number;
  contributesToSuccess: number;
  // Outcome Questions (245-248)
  chooseAgain: string;
  startAnotherBusiness: string;
  consideredEnding: string;
  lastThroughExit: string;
  // Experience & History (249-251)
  experiencedSplit: string;
  splitCauses: string[];
  workedWithCoach: string;
  // Open-Ended (252-263)
  mostValue: string;
  wishToChange: string;
  momentTested: string;
  momentGrateful: string;
  knowNow: string;
  adviceToOthers: string;
  neverSaid: string;
  whySucceedOrFail: string;
  anythingElse: string;
  equityDoDifferently: string;
  equityAdvice: string;
  crisisShowUp: string;
  // Follow-up & Permissions (264-267)
  takeAgainIn6Months: string;
  cofounderWilling: string;
  openToInterview: string;
  mayQuote: string;
}
const SECTIONS = [{
  id: 'demographics',
  label: 'About You',
  questions: '1-16'
}, {
  id: 'psychological-safety',
  label: 'Psychological Safety',
  questions: '17-23'
}, {
  id: 'trust',
  label: 'Trust',
  questions: '24-42'
}, {
  id: 'investment',
  label: 'Investment & Commitment',
  questions: '43-60'
}, {
  id: 'conflict',
  label: 'Conflict Style',
  questions: '62-81'
}, {
  id: 'communication',
  label: 'Communication',
  questions: '82-93'
}, {
  id: 'attachment',
  label: 'Attachment',
  questions: '94-105'
}, {
  id: 'personality',
  label: 'Personality',
  questions: '106-116'
}, {
  id: 'alignment',
  label: 'Vision & Role Alignment',
  questions: '117-124'
}, {
  id: 'decision-making',
  label: 'Decision-Making',
  questions: '125-130'
}, {
  id: 'power-dynamics',
  label: 'Power Dynamics',
  questions: '131-136'
}, {
  id: 'equity',
  label: 'Equity & Fairness',
  questions: '137-161'
}, {
  id: 'financial',
  label: 'Financial & Recognition',
  questions: '162-179'
}, {
  id: 'stress',
  label: 'Stress & Balance',
  questions: '180-199'
}, {
  id: 'crisis',
  label: 'Crisis & Values',
  questions: '200-213'
}, {
  id: 'dynamics',
  label: 'Partnership Dynamics',
  questions: '214-231'
}, {
  id: 'behavioral',
  label: 'Behavioral Indicators',
  questions: '232-238'
}, {
  id: 'outcomes',
  label: 'Outcomes',
  questions: '239-248'
}, {
  id: 'experience',
  label: 'Experience & History',
  questions: '249-251'
}, {
  id: 'reflections',
  label: 'Reflections',
  questions: '252-263'
}, {
  id: 'follow-up',
  label: 'Follow-up',
  questions: '264-267'
}];
export function CofounderSurvey({
  onClose
}: {
  onClose: () => void;
}) {
  const [currentSection, setCurrentSection] = useState('demographics');
  const [completedSections, setCompletedSections] = useState<Set<string>>(new Set());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<SurveyData>({
    email: '',
    yourName: '',
    cofounderNames: '',
    age: '',
    gender: '',
    education: '',
    functionalBackground: '',
    numberOfCofounders: '',
    title: '',
    companyStage: '',
    companyAge: '',
    timeKnownCofounder: '',
    howMetCofounder: '',
    workedTogetherBefore: '',
    firstTimeCofounder: '',
    mistakeHeldAgainst: 0,
    bringUpProblems: 0,
    rejectsOthersForDifferent: 0,
    safeToTakeRisk: 0,
    difficultToAskHelp: 0,
    wouldNotUndermine: 0,
    skillsValued: 0,
    cofounderCapable: 0,
    cofounderSuccessful: 0,
    cofounderKnowledgeable: 0,
    confidentInSkills: 0,
    specializedCapabilities: 0,
    notWellQualified: 0,
    concernedAboutWelfare: 0,
    needsImportant: 0,
    wouldNotHurt: 0,
    looksOutForMe: 0,
    willHelpMe: 0,
    senseOfJustice: 0,
    sticksToWord: 0,
    fairInDealings: 0,
    actionsNotConsistent: 0,
    likeValues: 0,
    soundPrinciples: 0,
    peopleDoWhatTheySay: 0,
    mustBeAlert: 0,
    satisfiedWithPartnership: 0,
    betterThanOthers: 0,
    closeToIdeal: 0,
    makesHappy: 0,
    fulfillsNeeds: 0,
    alternativesAttractive: 0,
    needsEasilyFulfilled: 0,
    couldFindAnother: 0,
    wouldDoFine: 0,
    putGreatDeal: 0,
    lifeLinked: 0,
    veryInvolved: 0,
    investedGreatDeal: 0,
    wantToLastLong: 0,
    committedToMaintaining: 0,
    notUpsetIfEnds: 0,
    workingIn3Years: 0,
    stronglyAttached: 0,
    attentionCheck1: 0,
    investigateIssue: 0,
    integrateIdeas: 0,
    workTogetherSolutions: 0,
    exchangeInformation: 0,
    satisfyNeeds: 0,
    accommodateWishes: 0,
    giveInToWishes: 0,
    goAlongWithSuggestions: 0,
    useInfluence: 0,
    useAuthority: 0,
    firmInPursuing: 0,
    usePower: 0,
    avoidBeingPutOnSpot: 0,
    avoidOpenDiscussion: 0,
    stayAwayFromDisagreement: 0,
    avoidUnpleasantExchanges: 0,
    findMiddleCourse: 0,
    proposeMiddleGround: 0,
    negotiateCompromise: 0,
    findFairCombination: 0,
    communicateWorkFrequently: 0,
    communicateNonWorkFrequently: 0,
    communicatesTimely: 0,
    infoReachesLate: 0,
    communicatesAccurately: 0,
    conflictingInfo: 0,
    blamesOthers: 0,
    workTogetherSolve: 0,
    sharedGoals: 0,
    understandConnection: 0,
    respectWork: 0,
    contributionsValued: 0,
    worryNotValued: 0,
    worryWantToEnd: 0,
    worryALot: 0,
    worryInterestedInOthers: 0,
    desireScares: 0,
    needReassurance: 0,
    preferNotShowFeelings: 0,
    comfortableSharingThoughts: 0,
    difficultToDepend: 0,
    nervousWhenClose: 0,
    preferNotEmotionallyConnected: 0,
    uncomfortableDiscussingFeelings: 0,
    extravertedEnthusiastic: 0,
    criticalQuarrelsome: 0,
    dependableSelfDisciplined: 0,
    anxiousEasilyUpset: 0,
    openToExperiences: 0,
    reservedQuiet: 0,
    sympatheticWarm: 0,
    disorganizedCareless: 0,
    calmEmotionallyStable: 0,
    conventionalUncreative: 0,
    commonVision: 0,
    discussedLongTerm: 0,
    differentDefinitions: 0,
    guessTop3Priorities: 0,
    rolesClearlyDefined: 0,
    confusionAboutResponsibility: 0,
    clearDecisionRights: 0,
    stepOnToes: 0,
    majorDecisions: '',
    disagreementResolution: '',
    tieBreaker: '',
    inputConsidered: 0,
    decisionsWithoutInput: 0,
    stuckOnDecisions: 0,
    seniorFounder: '',
    moreInfluence: '',
    equalWeight: 0,
    cofounderDominates: 0,
    holdBackOpinions: 0,
    treatedAsEquals: 0,
    equitySplit: '',
    equitySplitDecision: '',
    vestingSchedules: '',
    buybackClause: '',
    equitySplitFair: 0,
    stillFairToday: 0,
    equityResentment: 0,
    equityConversationOpen: 0,
    comfortableRaisingEquityConcerns: 0,
    advocateForInterests: 0,
    trustEquityConversation: 0,
    unresolvedEquityFeelings: 0,
    salariesFair: 0,
    compensationAligned: 0,
    financialSacrificesFair: 0,
    discussedFutureCompensation: 0,
    explicitEquityTalk: '',
    equityConversationDifficulty: '',
    renegotiatedEquity: '',
    equityTension: '',
    equityTensionResolved: '',
    wonderingFairShare: 0,
    thoughtAboutEquityIfBad: 0,
    worryEquityDamageRelationship: 0,
    believeFairRenegotiation: 0,
    similarFinancialSituations: '',
    similarRiskTolerance: 0,
    financialRunwayAffected: 0,
    alignedOnPayment: 0,
    financialStressCreatedTension: 0,
    understandCofounderConstraints: 0,
    creditSharedFairly: 0,
    cofounderGetsMoreRecognition: 0,
    cofounderCreditsMe: 0,
    iCreditCofounder: 0,
    externalRecognitionTension: 0,
    discussedPublicRepresentation: '',
    investorsAffectedRelationship: '',
    unitedFrontToInvestors: 0,
    disagreedInFrontOfInvestors: 0,
    employeePerceptionAffects: 0,
    alignedOnTeamCommunication: 0,
    advisorsHelpedRelationship: '',
    currentStressLevel: 0,
    burnoutLevel: 0,
    iWithdrawWhenStressed: 0,
    cofounderWithdrawsWhenStressed: 0,
    handleStressCompatibly: 0,
    stressBroughtCloser: 0,
    checkInOnWellbeing: 0,
    comfortableSharingStruggles: 0,
    similarLifeStages: '',
    similarWorkExpectations: 0,
    cofounderRespectsMyBoundaries: 0,
    iRespectCofounderBoundaries: 0,
    lifeDifferencesCreatedTension: 0,
    discussedLifeChanges: '',
    howWorkTogether: '',
    similarPace: 0,
    detailOrientedDifference: '',
    compatibleSchedules: 0,
    compatibleCommunication: 0,
    conversationFrequency: '',
    facedMajorCrisis: '',
    howHandledCrisis: '',
    crisisesStrengthened: 0,
    trustCofounderInCrisis: 0,
    hardestMomentShowUp: '',
    similarEthicalStandards: 0,
    discussedEthicalDilemmas: '',
    trustEthicalDecisions: 0,
    alignedOnTreatment: 0,
    discussedCannotContinue: '',
    discussedStepBack: '',
    confidentHandleMajorChange: 0,
    discussedSuccessionPlanning: '',
    discussedIfCompanyFails: '',
    similarExpectations: 0,
    notAsCommitted: 0,
    okayCarryingBusiness: 0,
    bouncedBackWell: 0,
    wayOfReconnecting: 0,
    predictReaction: 0,
    workingEnergizes: 0,
    feelMoreClear: 0,
    feelFrustratedDepleted: 0,
    spendTimeSocially: 0,
    metFriendsFamily: '',
    honestConversation: '',
    alliancesForm: '',
    clearLeader: '',
    decisionMakingComplicated: 0,
    moreSupport: 0,
    communicationHarder: 0,
    sameNumberOfCofounders: '',
    relationshipCheckIns: '',
    gaveConstructiveFeedback: '',
    receivedConstructiveFeedback: '',
    significantDisagreement: '',
    howResolved: '',
    formalAgreement: '',
    discussedLeaving: '',
    relationshipStrength: 0,
    likelyWorkingIn3Years: 0,
    cofounderSameAnswer: 0,
    satisfactionOverall: 0,
    survivesCrisis: 0,
    contributesToSuccess: 0,
    chooseAgain: '',
    startAnotherBusiness: '',
    consideredEnding: '',
    lastThroughExit: '',
    experiencedSplit: '',
    splitCauses: [],
    workedWithCoach: '',
    mostValue: '',
    wishToChange: '',
    momentTested: '',
    momentGrateful: '',
    knowNow: '',
    adviceToOthers: '',
    neverSaid: '',
    whySucceedOrFail: '',
    anythingElse: '',
    equityDoDifferently: '',
    equityAdvice: '',
    crisisShowUp: '',
    takeAgainIn6Months: '',
    cofounderWilling: '',
    openToInterview: '',
    mayQuote: ''
  });
  const [sessionId] = useState(() => `cofounder_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const autoSaveProgress = async () => {
    setIsSaving(true);
    try {
      const metadata = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        referrer: document.referrer || 'Direct'
      };
      const submissionData = {
        ...formData,
        sessionId,
        completedSections: Array.from(completedSections).join(', '),
        currentSection,
        isComplete: false,
        metadata
      };
      // Save to Google Sheets (primary)
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });
      // Save to Supabase (backup database)
      await saveSurveyResponse('cofounder_responses', {
        sessionId,
        surveyData: formData,
        isComplete: false,
        completedSections: Array.from(completedSections).join(', '),
        currentSection,
        metadata
      });
      console.log('✅ Progress auto-saved');
    } catch (error) {
      console.error('❌ Auto-save error:', error);
    } finally {
      setIsSaving(false);
    }
  };
  const markSectionComplete = (sectionId: string) => {
    setCompletedSections(prev => new Set([...prev, sectionId]));
  };
  const goToNextSection = async () => {
    markSectionComplete(currentSection);
    await autoSaveProgress();
    const currentIndex = SECTIONS.findIndex(s => s.id === currentSection);
    if (currentIndex < SECTIONS.length - 1) {
      setCurrentSection(SECTIONS[currentIndex + 1].id);
      window.scrollTo(0, 0);
    }
  };
  // NEW: Handle section tab clicks with auto-save
  const handleSectionChange = async (sectionId: string) => {
    // Save current progress before switching
    await autoSaveProgress();
    setCurrentSection(sectionId);
    window.scrollTo(0, 0);
  };
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const metadata = {
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        referrer: document.referrer || 'Direct'
      };
      const submissionData = {
        ...formData,
        sessionId,
        completedSections: Array.from(completedSections).join(', '),
        currentSection: 'follow-up',
        isComplete: true,
        metadata
      };
      // Save to Google Sheets (primary)
      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submissionData)
      });
      // Save to Supabase (backup database)
      await saveSurveyResponse('cofounder_responses', {
        sessionId,
        surveyData: formData,
        isComplete: true,
        completedSections: Array.from(completedSections).join(', '),
        currentSection: 'follow-up',
        metadata
      });
      setIsSubmitted(true);
      setTimeout(() => {
        window.location.href = '/';
      }, 3000);
    } catch (error) {
      console.error('❌ Submission error:', error);
      setSubmitError('There was an error submitting your response. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const renderLikertScale = (label: string, value: number, onChange: (value: number) => void, labels: string[] = ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree']) => <div className="mb-6" data-id="element-57">
      <p className="text-[#1A3D3A] mb-3" style={{
      fontFamily: "'DM Sans', sans-serif",
      fontSize: '15px'
    }} data-id="element-58">
        {label}
      </p>
      <div className="flex gap-2" data-id="element-59">
        {[1, 2, 3, 4, 5].map(num => <button key={num} onClick={() => onChange(num)} className={`flex-1 py-3 rounded-lg border-2 transition-all text-xs ${value === num ? 'bg-[#1A3D3A] text-white border-[#1A3D3A]' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40'}`} style={{
        fontFamily: "'DM Sans', sans-serif"
      }} data-id="element-60">
            {num}
          </button>)}
      </div>
      <div className="flex justify-between mt-2 text-xs text-[#3D5654]/70" style={{
      fontFamily: "'DM Sans', sans-serif"
    }} data-id="element-61">
        <span data-id="element-62">{labels[0]}</span>
        <span data-id="element-63">{labels[4]}</span>
      </div>
    </div>;
  const render1to10Scale = (label: string, value: number, onChange: (value: number) => void, leftLabel: string, rightLabel: string) => <div className="mb-6" data-id="element-64">
      <p className="text-[#1A3D3A] mb-3" style={{
      fontFamily: "'DM Sans', sans-serif",
      fontSize: '15px'
    }} data-id="element-65">
        {label}
      </p>
      <div className="flex gap-2" data-id="element-66">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => <button key={num} onClick={() => onChange(num)} className={`w-12 h-12 rounded-lg border-2 transition-all flex items-center justify-center ${value === num ? 'bg-[#1A3D3A] text-white border-[#1A3D3A] scale-110' : 'border-[#1A3D3A]/20 hover:border-[#1A3D3A]/40'}`} style={{
        fontFamily: "'DM Sans', sans-serif",
        fontSize: '16px',
        fontWeight: 600
      }} data-id="element-67">
            {num}
          </button>)}
      </div>
      <div className="flex justify-between mt-2 text-xs text-[#3D5654]/70" style={{
      fontFamily: "'DM Sans', sans-serif"
    }} data-id="element-68">
        <span data-id="element-69">{leftLabel}</span>
        <span data-id="element-70">{rightLabel}</span>
      </div>
    </div>;
  if (isSubmitted) {
    return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-id="element-71">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} className="bg-white rounded-[16px] p-12 max-w-md w-full text-center" data-id="element-72">
          <div className="w-16 h-16 bg-[#C5F467] rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-73">
            <Check className="w-8 h-8 text-[#1A3D3A]" data-id="element-74" />
          </div>
          <h2 className="text-[#1A3D3A] mb-4" style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: '32px',
          fontWeight: 500
        }} data-id="element-75">
            Thank You!
          </h2>
          <p className="text-[#3D5654] mb-8" style={{
          fontFamily: "'DM Sans', sans-serif",
          fontSize: '16px',
          lineHeight: 1.6
        }} data-id="element-76">
            Your insights will help us understand cofounder dynamics and support
            founders in building stronger partnerships.
          </p>
          <p className="text-[#3D5654] text-sm" style={{
          fontFamily: "'DM Sans', sans-serif",
          opacity: 0.7
        }} data-id="element-77">
            Redirecting you to the home page...
          </p>
        </motion.div>
      </div>;
  }
  const renderSection = () => {
    switch (currentSection) {
      case 'demographics':
        return <div className="space-y-6" data-id="element-78">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-79">
              About You & Your Partnership
            </h3>

            <div data-id="element-80">
              <label className="block text-[#1A3D3A] mb-2" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              fontWeight: 500
            }} data-id="element-81">
                Email Address *
              </label>
              <input type="email" value={formData.email} onChange={e => setFormData({
              ...formData,
              email: e.target.value
            })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="your@email.com" data-id="element-82" />
            </div>

            <div data-id="element-83">
              <label className="block text-[#1A3D3A] mb-2" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              fontWeight: 500
            }} data-id="element-84">
                Your name (Optional — for follow-up only)
              </label>
              <input type="text" value={formData.yourName} onChange={e => setFormData({
              ...formData,
              yourName: e.target.value
            })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Your name" data-id="element-85" />
            </div>

            <div data-id="element-86">
              <label className="block text-[#1A3D3A] mb-2" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              fontWeight: 500
            }} data-id="element-87">
                Your cofounder's name(s)
              </label>
              <input type="text" value={formData.cofounderNames} onChange={e => setFormData({
              ...formData,
              cofounderNames: e.target.value
            })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Cofounder name(s)" data-id="element-88" />
            </div>

            <SingleSelectDropdown label="What is your age?" options={['Under 25', '25–34', '35–44', '45–54', '55–64', '65+', 'Prefer not to say']} selected={formData.age} onChange={selected => setFormData({
            ...formData,
            age: selected
          })} placeholder="Select age range..." data-id="element-89" />

            <SingleSelectDropdown label="What is your gender?" options={['Male', 'Female', 'Non-binary', 'Other', 'Prefer not to say']} selected={formData.gender} onChange={selected => setFormData({
            ...formData,
            gender: selected
          })} placeholder="Select gender..." data-id="element-90" />

            <SingleSelectDropdown label="What is your highest level of education?" options={['High school', 'Some college', "Bachelor's degree", "Master's degree", 'MBA', 'PhD or Doctorate', 'Other professional degree']} selected={formData.education} onChange={selected => setFormData({
            ...formData,
            education: selected
          })} placeholder="Select education level..." data-id="element-91" />

            <SingleSelectDropdown label="What is your primary functional background?" options={['Technical (Engineering or Development)', 'Product', 'Design', 'Sales or Business Development', 'Marketing', 'Finance', 'Operations', 'Legal', 'Domain Expert', 'Generalist', 'Other']} selected={formData.functionalBackground} onChange={selected => setFormData({
            ...formData,
            functionalBackground: selected
          })} placeholder="Select background..." data-id="element-92" />

            <SingleSelectDropdown label="How many cofounders are on your team (including yourself)?" options={['2', '3', '4', '5 or more']} selected={formData.numberOfCofounders} onChange={selected => setFormData({
            ...formData,
            numberOfCofounders: selected
          })} placeholder="Select number..." data-id="element-93" />

            <SingleSelectDropdown label="What is your title or role?" options={['CEO', 'CTO', 'COO', 'CPO', 'CFO', 'Co-CEO (shared)', 'No formal titles', 'Other']} selected={formData.title} onChange={selected => setFormData({
            ...formData,
            title: selected
          })} placeholder="Select title..." data-id="element-94" />

            <SingleSelectDropdown label="What stage is your company at?" options={['Idea stage', 'MVP', 'Pre-revenue', 'Revenue (pre-funding)', 'Seed funded', 'Series A', 'Series B+', 'Scaling', 'Pre-exit', 'Exited', 'Company ended']} selected={formData.companyStage} onChange={selected => setFormData({
            ...formData,
            companyStage: selected
          })} placeholder="Select stage..." data-id="element-95" />

            <SingleSelectDropdown label="How long has your company been operating?" options={['Less than 6 months', '6–12 months', '1–2 years', '3–5 years', '5–10 years', 'More than 10 years']} selected={formData.companyAge} onChange={selected => setFormData({
            ...formData,
            companyAge: selected
          })} placeholder="Select duration..." data-id="element-96" />

            <SingleSelectDropdown label="How long have you known your cofounder before starting this company?" options={['Less than 3 months', '3–6 months', '6–12 months', '1–2 years', '3–5 years', '5–10 years', 'More than 10 years']} selected={formData.timeKnownCofounder} onChange={selected => setFormData({
            ...formData,
            timeKnownCofounder: selected
          })} placeholder="Select duration..." data-id="element-97" />

            <SingleSelectDropdown label="How did you meet your cofounder?" options={['Close friends', 'Previous colleagues', 'School or University', 'Networking event', 'Accelerator or Program', 'Cofounder matching platform', 'Introduced by others', 'Family', 'Romantic partners', 'Other']} selected={formData.howMetCofounder} onChange={selected => setFormData({
            ...formData,
            howMetCofounder: selected
          })} placeholder="Select how you met..." data-id="element-98" />

            <SingleSelectDropdown label="Had you worked together professionally before this venture?" options={['Yes — extensively (1+ years)', 'Yes — briefly', 'Yes — side project only', 'No']} selected={formData.workedTogetherBefore} onChange={selected => setFormData({
            ...formData,
            workedTogetherBefore: selected
          })} placeholder="Select answer..." data-id="element-99" />

            <SingleSelectDropdown label="Is this your first time as a cofounder?" options={['Yes — first time', 'No — second time', 'No — third time or more']} selected={formData.firstTimeCofounder} onChange={selected => setFormData({
            ...formData,
            firstTimeCofounder: selected
          })} placeholder="Select answer..." data-id="element-100" />
          </div>;
      case 'psychological-safety':
        return <div className="space-y-4" data-id="element-101">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-102">
              Psychological Safety
            </h3>

            {renderLikertScale('If I make a mistake in this partnership it is often held against me.', formData.mistakeHeldAgainst, value => setFormData({
            ...formData,
            mistakeHeldAgainst: value
          }))}
            {renderLikertScale('My cofounder and I are able to bring up problems and tough issues.', formData.bringUpProblems, value => setFormData({
            ...formData,
            bringUpProblems: value
          }))}
            {renderLikertScale('My cofounder sometimes rejects others for being different.', formData.rejectsOthersForDifferent, value => setFormData({
            ...formData,
            rejectsOthersForDifferent: value
          }))}
            {renderLikertScale('It is safe to take a risk in this partnership.', formData.safeToTakeRisk, value => setFormData({
            ...formData,
            safeToTakeRisk: value
          }))}
            {renderLikertScale('It is difficult to ask my cofounder for help.', formData.difficultToAskHelp, value => setFormData({
            ...formData,
            difficultToAskHelp: value
          }))}
            {renderLikertScale('My cofounder would never deliberately act in a way that undermines my efforts.', formData.wouldNotUndermine, value => setFormData({
            ...formData,
            wouldNotUndermine: value
          }))}
            {renderLikertScale('Working with my cofounder my unique skills and talents are valued and utilized.', formData.skillsValued, value => setFormData({
            ...formData,
            skillsValued: value
          }))}
          </div>;
      case 'trust':
        return <div className="space-y-4" data-id="element-103">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-104">
              Trust
            </h3>

            <div className="mb-8" data-id="element-105">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-106">
                Ability
              </h4>
              {renderLikertScale('My cofounder is very capable of performing their job.', formData.cofounderCapable, value => setFormData({
              ...formData,
              cofounderCapable: value
            }))}
              {renderLikertScale('My cofounder is known to be successful at the things they try to do.', formData.cofounderSuccessful, value => setFormData({
              ...formData,
              cofounderSuccessful: value
            }))}
              {renderLikertScale('My cofounder has much knowledge about the work that needs to be done.', formData.cofounderKnowledgeable, value => setFormData({
              ...formData,
              cofounderKnowledgeable: value
            }))}
              {renderLikertScale("I feel confident about my cofounder's skills.", formData.confidentInSkills, value => setFormData({
              ...formData,
              confidentInSkills: value
            }))}
              {renderLikertScale('My cofounder has specialized capabilities that can increase our performance.', formData.specializedCapabilities, value => setFormData({
              ...formData,
              specializedCapabilities: value
            }))}
              {renderLikertScale('My cofounder is not well qualified in their area of expertise.', formData.notWellQualified, value => setFormData({
              ...formData,
              notWellQualified: value
            }))}
            </div>

            <div className="mb-8" data-id="element-107">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-108">
                Benevolence
              </h4>
              {renderLikertScale('My cofounder is very concerned about my welfare.', formData.concernedAboutWelfare, value => setFormData({
              ...formData,
              concernedAboutWelfare: value
            }))}
              {renderLikertScale('My needs and desires are important to my cofounder.', formData.needsImportant, value => setFormData({
              ...formData,
              needsImportant: value
            }))}
              {renderLikertScale('My cofounder would not knowingly do anything to hurt me.', formData.wouldNotHurt, value => setFormData({
              ...formData,
              wouldNotHurt: value
            }))}
              {renderLikertScale('My cofounder really looks out for what is important to me.', formData.looksOutForMe, value => setFormData({
              ...formData,
              looksOutForMe: value
            }))}
              {renderLikertScale('My cofounder will go out of their way to help me.', formData.willHelpMe, value => setFormData({
              ...formData,
              willHelpMe: value
            }))}
            </div>

            <div className="mb-8" data-id="element-109">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-110">
                Integrity
              </h4>
              {renderLikertScale('My cofounder has a strong sense of justice.', formData.senseOfJustice, value => setFormData({
              ...formData,
              senseOfJustice: value
            }))}
              {renderLikertScale('I never have to wonder whether my cofounder will stick to their word.', formData.sticksToWord, value => setFormData({
              ...formData,
              sticksToWord: value
            }))}
              {renderLikertScale('My cofounder tries hard to be fair in dealings with others.', formData.fairInDealings, value => setFormData({
              ...formData,
              fairInDealings: value
            }))}
              {renderLikertScale("My cofounder's actions and behaviors are not very consistent.", formData.actionsNotConsistent, value => setFormData({
              ...formData,
              actionsNotConsistent: value
            }))}
              {renderLikertScale("I like my cofounder's values.", formData.likeValues, value => setFormData({
              ...formData,
              likeValues: value
            }))}
              {renderLikertScale("Sound principles seem to guide my cofounder's behavior.", formData.soundPrinciples, value => setFormData({
              ...formData,
              soundPrinciples: value
            }))}
            </div>

            <div data-id="element-111">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-112">
                Propensity to Trust
              </h4>
              {renderLikertScale('Most people can be counted on to do what they say they will do.', formData.peopleDoWhatTheySay, value => setFormData({
              ...formData,
              peopleDoWhatTheySay: value
            }))}
              {renderLikertScale('These days you must be alert or someone is likely to take advantage of you.', formData.mustBeAlert, value => setFormData({
              ...formData,
              mustBeAlert: value
            }))}
            </div>
          </div>;
      case 'investment':
        return <div className="space-y-4" data-id="element-113">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-114">
              Investment & Commitment
            </h3>

            <div className="mb-8" data-id="element-115">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-116">
                Satisfaction
              </h4>
              {renderLikertScale('I feel satisfied with our cofounder partnership.', formData.satisfiedWithPartnership, value => setFormData({
              ...formData,
              satisfiedWithPartnership: value
            }))}
              {renderLikertScale("Our partnership is much better than others' partnerships.", formData.betterThanOthers, value => setFormData({
              ...formData,
              betterThanOthers: value
            }))}
              {renderLikertScale('Our partnership is close to ideal.', formData.closeToIdeal, value => setFormData({
              ...formData,
              closeToIdeal: value
            }))}
              {renderLikertScale('Our partnership makes me very happy.', formData.makesHappy, value => setFormData({
              ...formData,
              makesHappy: value
            }))}
              {renderLikertScale('Our partnership does a good job of fulfilling my needs as a founder.', formData.fulfillsNeeds, value => setFormData({
              ...formData,
              fulfillsNeeds: value
            }))}
            </div>

            <div className="mb-8" data-id="element-117">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-118">
                Alternatives
              </h4>
              {renderLikertScale('My alternatives to this partnership are attractive to me (starting solo or finding a different cofounder etc.).', formData.alternativesAttractive, value => setFormData({
              ...formData,
              alternativesAttractive: value
            }))}
              {renderLikertScale('My needs for support collaboration and partnership could easily be fulfilled in an alternative situation.', formData.needsEasilyFulfilled, value => setFormData({
              ...formData,
              needsEasilyFulfilled: value
            }))}
              {renderLikertScale('I could find another suitable cofounder if this partnership ended.', formData.couldFindAnother, value => setFormData({
              ...formData,
              couldFindAnother: value
            }))}
              {renderLikertScale("If I weren't in this partnership I would do fine — I would find another good cofounder or succeed solo.", formData.wouldDoFine, value => setFormData({
              ...formData,
              wouldDoFine: value
            }))}
            </div>

            <div className="mb-8" data-id="element-119">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-120">
                Investment
              </h4>
              {renderLikertScale('I have put a great deal into our partnership that I would lose if the partnership ended.', formData.putGreatDeal, value => setFormData({
              ...formData,
              putGreatDeal: value
            }))}
              {renderLikertScale('Many aspects of my life have become linked to my cofounder (professional network reputation finances etc.).', formData.lifeLinked, value => setFormData({
              ...formData,
              lifeLinked: value
            }))}
              {renderLikertScale('I feel very involved in our partnership — like I have put a great deal into it.', formData.veryInvolved, value => setFormData({
              ...formData,
              veryInvolved: value
            }))}
              {renderLikertScale('Compared to other cofounders I have invested a great deal in this partnership.', formData.investedGreatDeal, value => setFormData({
              ...formData,
              investedGreatDeal: value
            }))}
            </div>

            <div data-id="element-121">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-122">
                Commitment
              </h4>
              {renderLikertScale('I want our partnership to last for a very long time.', formData.wantToLastLong, value => setFormData({
              ...formData,
              wantToLastLong: value
            }))}
              {renderLikertScale('I am committed to maintaining this cofounder partnership.', formData.committedToMaintaining, value => setFormData({
              ...formData,
              committedToMaintaining: value
            }))}
              {renderLikertScale('I would not feel very upset if our partnership were to end in the near future.', formData.notUpsetIfEnds, value => setFormData({
              ...formData,
              notUpsetIfEnds: value
            }))}
              {renderLikertScale('It is likely that I will be working with this cofounder in 3 years.', formData.workingIn3Years, value => setFormData({
              ...formData,
              workingIn3Years: value
            }))}
              {renderLikertScale('I feel strongly attached to this partnership.', formData.stronglyAttached, value => setFormData({
              ...formData,
              stronglyAttached: value
            }))}
            </div>

            <div className="mt-8 p-4 bg-[#C5F467]/20 rounded-lg" data-id="element-123">
              {renderLikertScale('ATTENTION CHECK: This question is to ensure you are reading carefully. Please select Agree (4) for this item.', formData.attentionCheck1, value => setFormData({
              ...formData,
              attentionCheck1: value
            }))}
            </div>
          </div>;
      case 'conflict':
        return <div className="space-y-4" data-id="element-124">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-125">
              Conflict Style
            </h3>

            <div className="mb-8" data-id="element-126">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-127">
                Integrating
              </h4>
              {renderLikertScale('I try to investigate an issue with my cofounder to find a solution acceptable to us both.', formData.investigateIssue, value => setFormData({
              ...formData,
              investigateIssue: value
            }))}
              {renderLikertScale("I try to integrate my ideas with my cofounder's to come up with a decision jointly.", formData.integrateIdeas, value => setFormData({
              ...formData,
              integrateIdeas: value
            }))}
              {renderLikertScale('I try to work with my cofounder to find solutions that satisfy both our expectations.', formData.workTogetherSolutions, value => setFormData({
              ...formData,
              workTogetherSolutions: value
            }))}
              {renderLikertScale('I exchange accurate information with my cofounder to solve problems together.', formData.exchangeInformation, value => setFormData({
              ...formData,
              exchangeInformation: value
            }))}
            </div>

            <div className="mb-8" data-id="element-128">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-129">
                Obliging
              </h4>
              {renderLikertScale('I generally try to satisfy the needs of my cofounder.', formData.satisfyNeeds, value => setFormData({
              ...formData,
              satisfyNeeds: value
            }))}
              {renderLikertScale('I usually accommodate the wishes of my cofounder.', formData.accommodateWishes, value => setFormData({
              ...formData,
              accommodateWishes: value
            }))}
              {renderLikertScale('I give in to the wishes of my cofounder.', formData.giveInToWishes, value => setFormData({
              ...formData,
              giveInToWishes: value
            }))}
              {renderLikertScale('I often go along with the suggestions of my cofounder.', formData.goAlongWithSuggestions, value => setFormData({
              ...formData,
              goAlongWithSuggestions: value
            }))}
            </div>

            <div className="mb-8" data-id="element-130">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-131">
                Dominating
              </h4>
              {renderLikertScale('I use my influence to get my ideas accepted.', formData.useInfluence, value => setFormData({
              ...formData,
              useInfluence: value
            }))}
              {renderLikertScale('I use my authority to make a decision in my favor.', formData.useAuthority, value => setFormData({
              ...formData,
              useAuthority: value
            }))}
              {renderLikertScale('I am generally firm in pursuing my side of the issue.', formData.firmInPursuing, value => setFormData({
              ...formData,
              firmInPursuing: value
            }))}
              {renderLikertScale('I sometimes use my power to win a competitive situation.', formData.usePower, value => setFormData({
              ...formData,
              usePower: value
            }))}
            </div>

            <div className="mb-8" data-id="element-132">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-133">
                Avoiding
              </h4>
              {renderLikertScale('I attempt to avoid being put on the spot and try to keep my conflict with my cofounder to myself.', formData.avoidBeingPutOnSpot, value => setFormData({
              ...formData,
              avoidBeingPutOnSpot: value
            }))}
              {renderLikertScale('I avoid open discussion of my differences with my cofounder.', formData.avoidOpenDiscussion, value => setFormData({
              ...formData,
              avoidOpenDiscussion: value
            }))}
              {renderLikertScale('I try to stay away from disagreement with my cofounder.', formData.stayAwayFromDisagreement, value => setFormData({
              ...formData,
              stayAwayFromDisagreement: value
            }))}
              {renderLikertScale('I try to avoid unpleasant exchanges with my cofounder.', formData.avoidUnpleasantExchanges, value => setFormData({
              ...formData,
              avoidUnpleasantExchanges: value
            }))}
            </div>

            <div data-id="element-134">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-135">
                Compromising
              </h4>
              {renderLikertScale('I try to find a middle course to resolve an impasse.', formData.findMiddleCourse, value => setFormData({
              ...formData,
              findMiddleCourse: value
            }))}
              {renderLikertScale('I usually propose a middle ground for breaking deadlocks.', formData.proposeMiddleGround, value => setFormData({
              ...formData,
              proposeMiddleGround: value
            }))}
              {renderLikertScale('I negotiate with my cofounder so that a compromise can be reached.', formData.negotiateCompromise, value => setFormData({
              ...formData,
              negotiateCompromise: value
            }))}
              {renderLikertScale('I try to find a fair combination of gains and losses for both of us.', formData.findFairCombination, value => setFormData({
              ...formData,
              findFairCombination: value
            }))}
            </div>
          </div>;
      case 'communication':
        return <div className="space-y-4" data-id="element-136">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-137">
              Communication
            </h3>

            <div className="mb-8" data-id="element-138">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-139">
                Frequency
              </h4>
              {renderLikertScale('We communicate frequently about work-related matters.', formData.communicateWorkFrequently, value => setFormData({
              ...formData,
              communicateWorkFrequently: value
            }))}
              {renderLikertScale('We communicate frequently about non-work matters.', formData.communicateNonWorkFrequently, value => setFormData({
              ...formData,
              communicateNonWorkFrequently: value
            }))}
            </div>

            <div className="mb-8" data-id="element-140">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-141">
                Timeliness
              </h4>
              {renderLikertScale('My cofounder communicates with me in a timely way.', formData.communicatesTimely, value => setFormData({
              ...formData,
              communicatesTimely: value
            }))}
              {renderLikertScale('Important information often reaches me too late to be useful.', formData.infoReachesLate, value => setFormData({
              ...formData,
              infoReachesLate: value
            }))}
            </div>

            <div className="mb-8" data-id="element-142">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-143">
                Accuracy
              </h4>
              {renderLikertScale('My cofounder communicates with me accurately.', formData.communicatesAccurately, value => setFormData({
              ...formData,
              communicatesAccurately: value
            }))}
              {renderLikertScale('I sometimes receive conflicting or unclear information from my cofounder.', formData.conflictingInfo, value => setFormData({
              ...formData,
              conflictingInfo: value
            }))}
            </div>

            <div className="mb-8" data-id="element-144">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-145">
                Problem-Solving
              </h4>
              {renderLikertScale('When problems occur my cofounder blames others rather than solving the problem.', formData.blamesOthers, value => setFormData({
              ...formData,
              blamesOthers: value
            }))}
              {renderLikertScale('When problems occur we work together to solve them.', formData.workTogetherSolve, value => setFormData({
              ...formData,
              workTogetherSolve: value
            }))}
            </div>

            <div className="mb-8" data-id="element-146">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-147">
                Shared Knowledge
              </h4>
              {renderLikertScale('My cofounder and I have shared goals for the company.', formData.sharedGoals, value => setFormData({
              ...formData,
              sharedGoals: value
            }))}
              {renderLikertScale('My cofounder and I understand how our work connects and depends on each other.', formData.understandConnection, value => setFormData({
              ...formData,
              understandConnection: value
            }))}
            </div>

            <div data-id="element-148">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-149">
                Mutual Respect
              </h4>
              {renderLikertScale("My cofounder and I respect each other's work.", formData.respectWork, value => setFormData({
              ...formData,
              respectWork: value
            }))}
              {renderLikertScale('My contributions are valued by my cofounder.', formData.contributionsValued, value => setFormData({
              ...formData,
              contributionsValued: value
            }))}
            </div>
          </div>;
      case 'attachment':
        return <div className="space-y-4" data-id="element-150">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-151">
              Attachment
            </h3>

            <div className="mb-8" data-id="element-152">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-153">
                Anxious Attachment
              </h4>
              {renderLikertScale("I worry that my cofounder doesn't value our partnership as much as I do.", formData.worryNotValued, value => setFormData({
              ...formData,
              worryNotValued: value
            }))}
              {renderLikertScale('I often worry that my cofounder will want to end our partnership.', formData.worryWantToEnd, value => setFormData({
              ...formData,
              worryWantToEnd: value
            }))}
              {renderLikertScale('I worry a lot about our partnership.', formData.worryALot, value => setFormData({
              ...formData,
              worryALot: value
            }))}
              {renderLikertScale('When my cofounder is not around I worry that they might become interested in working with someone else.', formData.worryInterestedInOthers, value => setFormData({
              ...formData,
              worryInterestedInOthers: value
            }))}
              {renderLikertScale('My desire to be closely connected sometimes scares my cofounder away.', formData.desireScares, value => setFormData({
              ...formData,
              desireScares: value
            }))}
              {renderLikertScale('I need a lot of reassurance that I am valued by my cofounder.', formData.needReassurance, value => setFormData({
              ...formData,
              needReassurance: value
            }))}
            </div>

            <div data-id="element-154">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-155">
                Avoidant Attachment
              </h4>
              {renderLikertScale('I prefer not to show my cofounder how I feel deep down.', formData.preferNotShowFeelings, value => setFormData({
              ...formData,
              preferNotShowFeelings: value
            }))}
              {renderLikertScale('I feel comfortable sharing my private thoughts and feelings with my cofounder.', formData.comfortableSharingThoughts, value => setFormData({
              ...formData,
              comfortableSharingThoughts: value
            }))}
              {renderLikertScale('I find it difficult to allow myself to depend on my cofounder.', formData.difficultToDepend, value => setFormData({
              ...formData,
              difficultToDepend: value
            }))}
              {renderLikertScale('I am nervous when my cofounder gets too close to me professionally.', formData.nervousWhenClose, value => setFormData({
              ...formData,
              nervousWhenClose: value
            }))}
              {renderLikertScale('I prefer not to be too emotionally connected to my cofounder.', formData.preferNotEmotionallyConnected, value => setFormData({
              ...formData,
              preferNotEmotionallyConnected: value
            }))}
              {renderLikertScale('I get uncomfortable when my cofounder wants to discuss feelings about our partnership.', formData.uncomfortableDiscussingFeelings, value => setFormData({
              ...formData,
              uncomfortableDiscussingFeelings: value
            }))}
            </div>
          </div>;
      case 'personality':
        return <div className="space-y-4" data-id="element-156">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-157">
              Personality (TIPI)
            </h3>

            <p className="text-[#3D5654] mb-6" style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '15px'
          }} data-id="element-158">
              Rate how much you agree with each statement about yourself:
            </p>

            {renderLikertScale('I see myself as: Extraverted and enthusiastic.', formData.extravertedEnthusiastic, value => setFormData({
            ...formData,
            extravertedEnthusiastic: value
          }))}
            {renderLikertScale('I see myself as: Critical and quarrelsome.', formData.criticalQuarrelsome, value => setFormData({
            ...formData,
            criticalQuarrelsome: value
          }))}
            {renderLikertScale('I see myself as: Dependable and self-disciplined.', formData.dependableSelfDisciplined, value => setFormData({
            ...formData,
            dependableSelfDisciplined: value
          }))}
            {renderLikertScale('I see myself as: Anxious and easily upset.', formData.anxiousEasilyUpset, value => setFormData({
            ...formData,
            anxiousEasilyUpset: value
          }))}
            {renderLikertScale('I see myself as: Open to new experiences and complex.', formData.openToExperiences, value => setFormData({
            ...formData,
            openToExperiences: value
          }))}
            {renderLikertScale('I see myself as: Reserved and quiet.', formData.reservedQuiet, value => setFormData({
            ...formData,
            reservedQuiet: value
          }))}
            {renderLikertScale('I see myself as: Sympathetic and warm.', formData.sympatheticWarm, value => setFormData({
            ...formData,
            sympatheticWarm: value
          }))}
            {renderLikertScale('I see myself as: Disorganized and careless.', formData.disorganizedCareless, value => setFormData({
            ...formData,
            disorganizedCareless: value
          }))}
            {renderLikertScale('I see myself as: Calm and emotionally stable.', formData.calmEmotionallyStable, value => setFormData({
            ...formData,
            calmEmotionallyStable: value
          }))}
            {renderLikertScale('I see myself as: Conventional and uncreative.', formData.conventionalUncreative, value => setFormData({
            ...formData,
            conventionalUncreative: value
          }))}

            <div className="mt-8 p-4 bg-[#C5F467]/20 rounded-lg" data-id="element-159">
              {renderLikertScale('ATTENTION CHECK: Please select Disagree (2) for this question to show you are reading carefully.', formData.attentionCheck2, value => setFormData({
              ...formData,
              attentionCheck2: value
            }))}
            </div>
          </div>;
      case 'alignment':
        return <div className="space-y-4" data-id="element-160">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-161">
              Vision & Role Alignment
            </h3>

            <div className="mb-8" data-id="element-162">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-163">
                Vision Alignment
              </h4>
              {renderLikertScale('We share a common vision for what we are trying to build.', formData.commonVision, value => setFormData({
              ...formData,
              commonVision: value
            }))}
              {renderLikertScale('We have explicitly discussed our long-term objectives (e.g. exit strategy) and are aligned.', formData.discussedLongTerm, value => setFormData({
              ...formData,
              discussedLongTerm: value
            }))}
              {renderLikertScale('We have different definitions of what success looks like for this company.', formData.differentDefinitions, value => setFormData({
              ...formData,
              differentDefinitions: value
            }))}
              {renderLikertScale("If I had to guess my cofounder's top 3 priorities this quarter I would be mostly right.", formData.guessTop3Priorities, value => setFormData({
              ...formData,
              guessTop3Priorities: value
            }))}
            </div>

            <div data-id="element-164">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-165">
                Role Clarity
              </h4>
              {renderLikertScale('Our respective roles and responsibilities are clearly defined.', formData.rolesClearlyDefined, value => setFormData({
              ...formData,
              rolesClearlyDefined: value
            }))}
              {renderLikertScale('There is confusion about who is responsible for what in our partnership.', formData.confusionAboutResponsibility, value => setFormData({
              ...formData,
              confusionAboutResponsibility: value
            }))}
              {renderLikertScale('We have clear decision rights — we know who has final say on what.', formData.clearDecisionRights, value => setFormData({
              ...formData,
              clearDecisionRights: value
            }))}
              {renderLikertScale("We often step on each other's toes in daily work.", formData.stepOnToes, value => setFormData({
              ...formData,
              stepOnToes: value
            }))}
            </div>
          </div>;
      case 'decision-making':
        return <div className="space-y-6" data-id="element-166">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-167">
              Decision-Making
            </h3>

            <SingleSelectDropdown label="How do you typically make major decisions together?" options={['One person decides most things', 'Consensus required on everything', 'Domain-based (each owns their area)', 'We struggle to decide', 'Depends on the decision']} selected={formData.majorDecisions} onChange={selected => setFormData({
            ...formData,
            majorDecisions: selected
          })} placeholder="Select approach..." data-id="element-168" />

            <SingleSelectDropdown label="When you disagree on something important how is it usually resolved?" options={['We discuss until we agree', 'One person usually gives in', 'We compromise', 'We avoid the decision', 'We escalate to advisors or board', 'It depends on who cares more']} selected={formData.disagreementResolution} onChange={selected => setFormData({
            ...formData,
            disagreementResolution: selected
          })} placeholder="Select resolution..." data-id="element-169" />

            <SingleSelectDropdown label="Do you have a clear tie-breaker mechanism when you can't agree?" options={['Yes — one person has final say', 'Yes — we have a process', 'No — we figure it out each time', 'No — and it causes problems']} selected={formData.tieBreaker} onChange={selected => setFormData({
            ...formData,
            tieBreaker: selected
          })} placeholder="Select answer..." data-id="element-170" />

            {renderLikertScale('How often do you feel your input is genuinely considered before decisions are made?', formData.inputConsidered, value => setFormData({
            ...formData,
            inputConsidered: value
          }), ['Never', 'Rarely', 'Sometimes', 'Often', 'Always'])}
            {renderLikertScale('I sometimes feel decisions are made without adequate input from me.', formData.decisionsWithoutInput, value => setFormData({
            ...formData,
            decisionsWithoutInput: value
          }))}
            {renderLikertScale("We have gotten stuck or delayed on important decisions because we couldn't agree.", formData.stuckOnDecisions, value => setFormData({
            ...formData,
            stuckOnDecisions: value
          }))}
          </div>;
      case 'power-dynamics':
        return <div className="space-y-6" data-id="element-171">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-172">
              Power Dynamics
            </h3>

            <SingleSelectDropdown label="Is there a senior or lead founder in your partnership?" options={['Yes — me', 'Yes — my cofounder', "No — we're truly equal", "It's unclear"]} selected={formData.seniorFounder} onChange={selected => setFormData({
            ...formData,
            seniorFounder: selected
          })} placeholder="Select answer..." data-id="element-173" />

            <SingleSelectDropdown label="Who typically has more influence in major decisions?" options={['Me', 'My cofounder', 'Equal', 'It depends on the topic', 'Unclear']} selected={formData.moreInfluence} onChange={selected => setFormData({
            ...formData,
            moreInfluence: selected
          })} placeholder="Select answer..." data-id="element-174" />

            {renderLikertScale("I feel my voice carries equal weight to my cofounder's.", formData.equalWeight, value => setFormData({
            ...formData,
            equalWeight: value
          }))}
            {renderLikertScale('My cofounder sometimes dominates decisions or conversations.', formData.cofounderDominates, value => setFormData({
            ...formData,
            cofounderDominates: value
          }))}
            {renderLikertScale('I sometimes hold back my opinions to avoid conflict.', formData.holdBackOpinions, value => setFormData({
            ...formData,
            holdBackOpinions: value
          }))}
            {renderLikertScale('External parties (investors employees) treat us as equals.', formData.treatedAsEquals, value => setFormData({
            ...formData,
            treatedAsEquals: value
          }))}
          </div>;
      case 'equity':
        return <div className="space-y-6" data-id="element-175">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-176">
              Equity & Fairness
            </h3>

            <div className="mb-8" data-id="element-177">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-178">
                Equity Structure
              </h4>

              <SingleSelectDropdown label="How is equity split between cofounders?" options={['Equal (50-50 or equal shares)', 'Nearly equal (within 5%)', 'Somewhat unequal (60-40 type)', 'Significantly unequal (70-30 or more)', 'Very unequal (80-20 or more)', 'Prefer not to say']} selected={formData.equitySplit} onChange={selected => setFormData({
              ...formData,
              equitySplit: selected
            })} placeholder="Select split..." data-id="element-179" />

              <SingleSelectDropdown label="How was the equity split decided?" options={["Equal by default — we didn't discuss it much", 'Negotiated based on multiple factors', 'Based on who had the original idea', 'Based on who joined first', 'Based on capital contributed', 'Based on expected time commitment', 'Based on skills and experience', 'One person proposed and the other accepted', 'It was contentious', 'Other']} selected={formData.equitySplitDecision} onChange={selected => setFormData({
              ...formData,
              equitySplitDecision: selected
            })} placeholder="Select how decided..." data-id="element-180" />

              <SingleSelectDropdown label="Do you have vesting schedules in place for cofounder equity?" options={['Yes — standard 4-year with 1-year cliff', 'Yes — customized vesting terms', 'Partial — some terms in place', 'No — but we plan to', "No — and we don't plan to", 'Unsure']} selected={formData.vestingSchedules} onChange={selected => setFormData({
              ...formData,
              vestingSchedules: selected
            })} placeholder="Select answer..." data-id="element-181" />

              <SingleSelectDropdown label="Is there a buyback or repurchase clause if a cofounder leaves?" options={['Yes — clearly defined', 'Yes — but vague', 'No — but we should have one', 'No', 'Unsure']} selected={formData.buybackClause} onChange={selected => setFormData({
              ...formData,
              buybackClause: selected
            })} placeholder="Select answer..." data-id="element-182" />
            </div>

            <div className="mb-8" data-id="element-183">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-184">
                Equity Fairness
              </h4>
              {renderLikertScale('I feel our equity split is fair given our respective contributions.', formData.equitySplitFair, value => setFormData({
              ...formData,
              equitySplitFair: value
            }))}
              {renderLikertScale("If equity were being split today based on actual contributions I would still feel it's fair.", formData.stillFairToday, value => setFormData({
              ...formData,
              stillFairToday: value
            }))}
              {renderLikertScale('I sometimes feel resentment about how equity is divided.', formData.equityResentment, value => setFormData({
              ...formData,
              equityResentment: value
            }))}
              {renderLikertScale('The equity conversation was handled openly and respectfully when we started.', formData.equityConversationOpen, value => setFormData({
              ...formData,
              equityConversationOpen: value
            }))}
              {renderLikertScale('I feel comfortable raising concerns about equity or compensation with my cofounder.', formData.comfortableRaisingEquityConcerns, value => setFormData({
              ...formData,
              comfortableRaisingEquityConcerns: value
            }))}
              {renderLikertScale("I believe my cofounder would advocate for my equity interests even if I weren't in the room.", formData.advocateForInterests, value => setFormData({
              ...formData,
              advocateForInterests: value
            }))}
              {renderLikertScale('If our roles or contributions changed significantly I trust we could have a fair conversation about adjusting equity.', formData.trustEquityConversation, value => setFormData({
              ...formData,
              trustEquityConversation: value
            }))}
              {renderLikertScale('There are unresolved feelings or conversations about equity between us.', formData.unresolvedEquityFeelings, value => setFormData({
              ...formData,
              unresolvedEquityFeelings: value
            }))}
            </div>

            <div className="mb-8" data-id="element-185">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-186">
                Compensation
              </h4>
              {renderLikertScale('Our salaries (or lack of salaries) feel fair relative to each other.', formData.salariesFair, value => setFormData({
              ...formData,
              salariesFair: value
            }))}
              {renderLikertScale('We are aligned on compensation philosophy — when to pay ourselves and how much.', formData.compensationAligned, value => setFormData({
              ...formData,
              compensationAligned: value
            }))}
              {renderLikertScale('Financial sacrifices (low salary personal investment etc.) are being shared fairly between us.', formData.financialSacrificesFair, value => setFormData({
              ...formData,
              financialSacrificesFair: value
            }))}
              {renderLikertScale('We have discussed what fair compensation looks like as the company grows.', formData.discussedFutureCompensation, value => setFormData({
              ...formData,
              discussedFutureCompensation: value
            }))}
            </div>

            <div className="mb-8" data-id="element-187">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-188">
                Equity Discussions
              </h4>

              <SingleSelectDropdown label="Before starting the company did you have an explicit equity talk about split vesting and what-ifs?" options={['Yes — detailed and documented', 'Yes — discussed but informal', 'Briefly touched on it', 'No — we assumed it would work out', 'No — and it caused problems later']} selected={formData.explicitEquityTalk} onChange={selected => setFormData({
              ...formData,
              explicitEquityTalk: selected
            })} placeholder="Select answer..." data-id="element-189" />

              <SingleSelectDropdown label="How difficult was it to have the initial equity conversation?" options={['Very easy — we agreed quickly', 'Somewhat easy', 'Neutral', 'Somewhat difficult', 'Very difficult — significant tension', "We still haven't fully resolved it"]} selected={formData.equityConversationDifficulty} onChange={selected => setFormData({
              ...formData,
              equityConversationDifficulty: selected
            })} placeholder="Select difficulty..." data-id="element-190" />

              <SingleSelectDropdown label="Have you ever renegotiated or adjusted the equity split since founding?" options={['Yes — and it went well', 'Yes — and it was difficult', "We've discussed it but made no changes", 'No — but we should', "No — it hasn't been needed"]} selected={formData.renegotiatedEquity} onChange={selected => setFormData({
              ...formData,
              renegotiatedEquity: selected
            })} placeholder="Select answer..." data-id="element-191" />

              <SingleSelectDropdown label="Have equity or compensation issues ever caused significant tension between you?" options={['Yes — major ongoing tension', 'Yes — significant tension in the past', 'Yes — minor friction', 'No — never an issue']} selected={formData.equityTension} onChange={selected => setFormData({
              ...formData,
              equityTension: selected
            })} placeholder="Select answer..." data-id="element-192" />

              <SingleSelectDropdown label="If you've had equity tension how was it resolved?" options={['Fully resolved through open discussion', 'Partially resolved', 'We avoid talking about it', 'Still unresolved', 'Not applicable — no tension']} selected={formData.equityTensionResolved} onChange={selected => setFormData({
              ...formData,
              equityTensionResolved: selected
            })} placeholder="Select resolution..." data-id="element-193" />
            </div>

            <div data-id="element-194">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-195">
                Equity Red Flags
              </h4>
              {renderLikertScale("I sometimes wonder if I'm getting my fair share.", formData.wonderingFairShare, value => setFormData({
              ...formData,
              wonderingFairShare: value
            }))}
              {renderLikertScale('I have thought about what would happen to my equity if things went badly.', formData.thoughtAboutEquityIfBad, value => setFormData({
              ...formData,
              thoughtAboutEquityIfBad: value
            }))}
              {renderLikertScale('I worry that equity discussions could damage our relationship.', formData.worryEquityDamageRelationship, value => setFormData({
              ...formData,
              worryEquityDamageRelationship: value
            }))}
              {renderLikertScale('I believe both of us would handle an equity renegotiation fairly if it became necessary.', formData.believeFairRenegotiation, value => setFormData({
              ...formData,
              believeFairRenegotiation: value
            }))}
            </div>
          </div>;
      case 'financial':
        return <div className="space-y-6" data-id="element-196">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-197">
              Financial & Recognition
            </h3>

            <div className="mb-8" data-id="element-198">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-199">
                Financial & Risk
              </h4>

              <SingleSelectDropdown label="Do you and your cofounder have similar personal financial situations?" options={['Very similar', 'Somewhat similar', 'Different', 'Very different', "Don't know"]} selected={formData.similarFinancialSituations} onChange={selected => setFormData({
              ...formData,
              similarFinancialSituations: selected
            })} placeholder="Select similarity..." data-id="element-200" />

              {renderLikertScale('We have similar risk tolerances when it comes to business decisions.', formData.similarRiskTolerance, value => setFormData({
              ...formData,
              similarRiskTolerance: value
            }))}
              {renderLikertScale('Differences in personal financial runway have affected our decision-making.', formData.financialRunwayAffected, value => setFormData({
              ...formData,
              financialRunwayAffected: value
            }))}
              {renderLikertScale('We are aligned on when and how much to pay ourselves.', formData.alignedOnPayment, value => setFormData({
              ...formData,
              alignedOnPayment: value
            }))}
              {renderLikertScale('Financial stress (personal or business) has created tension between us.', formData.financialStressCreatedTension, value => setFormData({
              ...formData,
              financialStressCreatedTension: value
            }))}
              {renderLikertScale("I understand my cofounder's personal financial constraints and needs.", formData.understandCofounderConstraints, value => setFormData({
              ...formData,
              understandCofounderConstraints: value
            }))}
            </div>

            <div className="mb-8" data-id="element-201">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-202">
                Recognition & Credit
              </h4>
              {renderLikertScale('Credit and recognition are shared fairly between us.', formData.creditSharedFairly, value => setFormData({
              ...formData,
              creditSharedFairly: value
            }))}
              {renderLikertScale('I sometimes feel my cofounder gets more recognition than they deserve.', formData.cofounderGetsMoreRecognition, value => setFormData({
              ...formData,
              cofounderGetsMoreRecognition: value
            }))}
              {renderLikertScale('My cofounder actively credits me when speaking to others.', formData.cofounderCreditsMe, value => setFormData({
              ...formData,
              cofounderCreditsMe: value
            }))}
              {renderLikertScale('I actively credit my cofounder when speaking to others.', formData.iCreditCofounder, value => setFormData({
              ...formData,
              iCreditCofounder: value
            }))}
              {renderLikertScale('External recognition (press awards speaking opportunities) has created tension between us.', formData.externalRecognitionTension, value => setFormData({
              ...formData,
              externalRecognitionTension: value
            }))}

              <SingleSelectDropdown label="We have discussed how to handle media speaking and public representation." options={['Yes', 'No', 'Not applicable yet']} selected={formData.discussedPublicRepresentation} onChange={selected => setFormData({
              ...formData,
              discussedPublicRepresentation: selected
            })} placeholder="Select answer..." data-id="element-203" />
            </div>

            <div data-id="element-204">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-205">
                External Stakeholders
              </h4>

              <SingleSelectDropdown label="Have investors or board members ever affected your cofounder relationship?" options={['Yes — positively', 'Yes — negatively', 'Yes — both', 'No significant impact', 'Not applicable']} selected={formData.investorsAffectedRelationship} onChange={selected => setFormData({
              ...formData,
              investorsAffectedRelationship: selected
            })} placeholder="Select impact..." data-id="element-206" />

              {renderLikertScale('Do you present a united front to investors and the board?', formData.unitedFrontToInvestors, value => setFormData({
              ...formData,
              unitedFrontToInvestors: value
            }))}
              {renderLikertScale('Have you ever disagreed in front of investors or board members in a way that was uncomfortable?', formData.disagreedInFrontOfInvestors, value => setFormData({
              ...formData,
              disagreedInFrontOfInvestors: value
            }))}
              {renderLikertScale('How employees perceive our partnership affects how we work together.', formData.employeePerceptionAffects, value => setFormData({
              ...formData,
              employeePerceptionAffects: value
            }))}
              {renderLikertScale('We are aligned on how to communicate with our team about company challenges.', formData.alignedOnTeamCommunication, value => setFormData({
              ...formData,
              alignedOnTeamCommunication: value
            }))}

              <SingleSelectDropdown label="External advisors or mentors have helped strengthen our cofounder relationship." options={['Yes', 'No', "We don't have advisors", 'Not applicable']} selected={formData.advisorsHelpedRelationship} onChange={selected => setFormData({
              ...formData,
              advisorsHelpedRelationship: selected
            })} placeholder="Select answer..." data-id="element-207" />
            </div>
          </div>;
      case 'stress':
        return <div className="space-y-6" data-id="element-208">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-209">
              Stress & Balance
            </h3>

            <div className="mb-8" data-id="element-210">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-211">
                Stress & Burnout
              </h4>

              {render1to10Scale('On a scale of 1–10 how stressed are you currently about the business?', formData.currentStressLevel, value => setFormData({
              ...formData,
              currentStressLevel: value
            }), 'Not Stressed At All', 'Extremely Stressed')}
              {render1to10Scale('On a scale of 1–10 how close have you come to burnout in this venture?', formData.burnoutLevel, value => setFormData({
              ...formData,
              burnoutLevel: value
            }), 'Not At All', 'Completely Burned Out')}

              {renderLikertScale("When I'm stressed I tend to withdraw or become less communicative.", formData.iWithdrawWhenStressed, value => setFormData({
              ...formData,
              iWithdrawWhenStressed: value
            }))}
              {renderLikertScale('When my cofounder is stressed they tend to withdraw or become less communicative.', formData.cofounderWithdrawsWhenStressed, value => setFormData({
              ...formData,
              cofounderWithdrawsWhenStressed: value
            }))}
              {renderLikertScale('We handle stress in compatible ways.', formData.handleStressCompatibly, value => setFormData({
              ...formData,
              handleStressCompatibly: value
            }))}
              {renderLikertScale('Stressful periods have brought us closer together rather than driving us apart.', formData.stressBroughtCloser, value => setFormData({
              ...formData,
              stressBroughtCloser: value
            }))}
              {renderLikertScale("We check in on each other's wellbeing not just work progress.", formData.checkInOnWellbeing, value => setFormData({
              ...formData,
              checkInOnWellbeing: value
            }))}
              {renderLikertScale("I feel comfortable telling my cofounder when I'm struggling or overwhelmed.", formData.comfortableSharingStruggles, value => setFormData({
              ...formData,
              comfortableSharingStruggles: value
            }))}
            </div>

            <div className="mb-8" data-id="element-212">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-213">
                Work-Life Balance
              </h4>

              <SingleSelectDropdown label="Are you and your cofounder in similar life stages?" options={['Yes — very similar', 'Somewhat similar', 'Different stages', 'Very different']} selected={formData.similarLifeStages} onChange={selected => setFormData({
              ...formData,
              similarLifeStages: selected
            })} placeholder="Select similarity..." data-id="element-214" />

              {renderLikertScale('Do you have similar expectations about work hours and availability?', formData.similarWorkExpectations, value => setFormData({
              ...formData,
              similarWorkExpectations: value
            }))}
              {renderLikertScale('My cofounder respects my personal time and boundaries.', formData.cofounderRespectsMyBoundaries, value => setFormData({
              ...formData,
              cofounderRespectsMyBoundaries: value
            }))}
              {renderLikertScale("I respect my cofounder's personal time and boundaries.", formData.iRespectCofounderBoundaries, value => setFormData({
              ...formData,
              iRespectCofounderBoundaries: value
            }))}
              {renderLikertScale('Differences in life circumstances (family health finances) have created tension.', formData.lifeDifferencesCreatedTension, value => setFormData({
              ...formData,
              lifeDifferencesCreatedTension: value
            }))}

              <SingleSelectDropdown label="We have discussed how personal life changes (marriage kids health) might affect our partnership." options={['Yes — in depth', 'Yes — briefly', 'No']} selected={formData.discussedLifeChanges} onChange={selected => setFormData({
              ...formData,
              discussedLifeChanges: selected
            })} placeholder="Select answer..." data-id="element-215" />
            </div>

            <div data-id="element-216">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-217">
                Working Styles
              </h4>

              <SingleSelectDropdown label="How do you primarily work together?" options={['Same office daily', 'Same office sometimes', 'Fully remote', 'Different cities or time zones']} selected={formData.howWorkTogether} onChange={selected => setFormData({
              ...formData,
              howWorkTogether: selected
            })} placeholder="Select arrangement..." data-id="element-218" />

              {renderLikertScale('We move at a similar pace when making decisions and taking action.', formData.similarPace, value => setFormData({
              ...formData,
              similarPace: value
            }))}

              <SingleSelectDropdown label="One of us is significantly more detail-oriented while the other is more big-picture." options={['Yes — and it works well', 'Yes — and it causes friction', "No — we're similar"]} selected={formData.detailOrientedDifference} onChange={selected => setFormData({
              ...formData,
              detailOrientedDifference: selected
            })} placeholder="Select answer..." data-id="element-219" />

              {renderLikertScale('Our working hours and schedules are compatible.', formData.compatibleSchedules, value => setFormData({
              ...formData,
              compatibleSchedules: value
            }))}
              {renderLikertScale('We have compatible communication preferences (frequency medium responsiveness).', formData.compatibleCommunication, value => setFormData({
              ...formData,
              compatibleCommunication: value
            }))}

              <SingleSelectDropdown label="How often do you have synchronous (real-time) conversations with your cofounder?" options={['Multiple times daily', 'Daily', 'Few times a week', 'Weekly', 'Less than weekly']} selected={formData.conversationFrequency} onChange={selected => setFormData({
              ...formData,
              conversationFrequency: selected
            })} placeholder="Select frequency..." data-id="element-220" />
            </div>
          </div>;
      case 'crisis':
        return <div className="space-y-6" data-id="element-221">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-222">
              Crisis & Values
            </h3>

            <div className="mb-8" data-id="element-223">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-224">
                Crisis Response
              </h4>

              <SingleSelectDropdown label="Have you faced a major crisis together (funding falling through key employee leaving product failure etc.)?" options={['Yes — multiple', 'Yes — one major', 'Minor setbacks only', 'No']} selected={formData.facedMajorCrisis} onChange={selected => setFormData({
              ...formData,
              facedMajorCrisis: selected
            })} placeholder="Select answer..." data-id="element-225" />

              <SingleSelectDropdown label="If yes how did you handle it?" options={['Came together stronger', 'Managed it but strained', 'Struggled significantly', 'Nearly broke us', 'Not applicable']} selected={formData.howHandledCrisis} onChange={selected => setFormData({
              ...formData,
              howHandledCrisis: selected
            })} placeholder="Select outcome..." data-id="element-226" />

              {renderLikertScale('Crises have strengthened rather than weakened our partnership.', formData.crisisesStrengthened, value => setFormData({
              ...formData,
              crisisesStrengthened: value
            }))}
              {renderLikertScale('I trust my cofounder to have my back when things go wrong.', formData.trustCofounderInCrisis, value => setFormData({
              ...formData,
              trustCofounderInCrisis: value
            }))}

              <div data-id="element-227">
                <label className="block text-[#1A3D3A] mb-2" style={{
                fontFamily: "'DM Sans', sans-serif",
                fontSize: '15px',
                fontWeight: 500
              }} data-id="element-228">
                  During the hardest moment of your startup journey how did your
                  cofounder show up?
                </label>
                <textarea value={formData.hardestMomentShowUp} onChange={e => setFormData({
                ...formData,
                hardestMomentShowUp: e.target.value
              })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
                fontFamily: "'DM Sans', sans-serif"
              }} rows={4} placeholder="Describe how your cofounder showed up..." data-id="element-229" />
              </div>
            </div>

            <div className="mb-8" data-id="element-230">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-231">
                Values & Ethics
              </h4>

              {renderLikertScale('We share similar ethical standards for how we run the business.', formData.similarEthicalStandards, value => setFormData({
              ...formData,
              similarEthicalStandards: value
            }))}

              <SingleSelectDropdown label="We have discussed how we would handle ethical dilemmas." options={['Yes', 'No']} selected={formData.discussedEthicalDilemmas} onChange={selected => setFormData({
              ...formData,
              discussedEthicalDilemmas: selected
            })} placeholder="Select answer..." data-id="element-232" />

              {renderLikertScale('I trust my cofounder to make ethical decisions even under pressure.', formData.trustEthicalDecisions, value => setFormData({
              ...formData,
              trustEthicalDecisions: value
            }))}
              {renderLikertScale('We are aligned on how we treat employees customers and partners.', formData.alignedOnTreatment, value => setFormData({
              ...formData,
              alignedOnTreatment: value
            }))}
            </div>

            <div data-id="element-233">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-234">
                Future Planning
              </h4>

              <SingleSelectDropdown label="We have discussed what happens if one of us can't continue (illness personal crisis)." options={['Yes — detailed plan', 'Yes — general understanding', 'Briefly', 'No']} selected={formData.discussedCannotContinue} onChange={selected => setFormData({
              ...formData,
              discussedCannotContinue: selected
            })} placeholder="Select answer..." data-id="element-235" />

              <SingleSelectDropdown label="We have discussed potential scenarios where one of us might want to step back or reduce involvement." options={['Yes', 'No']} selected={formData.discussedStepBack} onChange={selected => setFormData({
              ...formData,
              discussedStepBack: selected
            })} placeholder="Select answer..." data-id="element-236" />

              {renderLikertScale("I am confident we could handle a major change in one cofounder's circumstances.", formData.confidentHandleMajorChange, value => setFormData({
              ...formData,
              confidentHandleMajorChange: value
            }))}

              <SingleSelectDropdown label="We have discussed succession planning or leadership transitions." options={['Yes', 'No', 'Too early']} selected={formData.discussedSuccessionPlanning} onChange={selected => setFormData({
              ...formData,
              discussedSuccessionPlanning: selected
            })} placeholder="Select answer..." data-id="element-237" />

              <SingleSelectDropdown label="We have discussed what happens to the partnership if the company fails." options={['Yes', 'No']} selected={formData.discussedIfCompanyFails} onChange={selected => setFormData({
              ...formData,
              discussedIfCompanyFails: selected
            })} placeholder="Select answer..." data-id="element-238" />
            </div>
          </div>;
      case 'dynamics':
        return <div className="space-y-6" data-id="element-239">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-240">
              Partnership Dynamics
            </h3>

            <div className="mb-8" data-id="element-241">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-242">
                Commitment Alignment
              </h4>
              {renderLikertScale('We have similar expectations about how much time and effort each of us dedicates to the venture.', formData.similarExpectations, value => setFormData({
              ...formData,
              similarExpectations: value
            }))}
              {renderLikertScale("I sometimes feel my cofounder isn't as committed as I am.", formData.notAsCommitted, value => setFormData({
              ...formData,
              notAsCommitted: value
            }))}
              {renderLikertScale('If my cofounder needed a few months off I would feel okay carrying the business temporarily.', formData.okayCarryingBusiness, value => setFormData({
              ...formData,
              okayCarryingBusiness: value
            }))}
            </div>

            <div className="mb-8" data-id="element-243">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-244">
                Resilience & Recovery
              </h4>
              {renderLikertScale('We have bounced back well after major disagreements or setbacks.', formData.bouncedBackWell, value => setFormData({
              ...formData,
              bouncedBackWell: value
            }))}
              {renderLikertScale('We have a way of reconnecting when things get tense.', formData.wayOfReconnecting, value => setFormData({
              ...formData,
              wayOfReconnecting: value
            }))}
              {renderLikertScale('I can predict how my cofounder will react under stress.', formData.predictReaction, value => setFormData({
              ...formData,
              predictReaction: value
            }))}
            </div>

            <div className="mb-8" data-id="element-245">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-246">
                Energy & Regeneration
              </h4>
              {renderLikertScale('Working with my cofounder energizes me more often than it drains me.', formData.workingEnergizes, value => setFormData({
              ...formData,
              workingEnergizes: value
            }))}
              {renderLikertScale('After we talk I usually feel more clear motivated or focused.', formData.feelMoreClear, value => setFormData({
              ...formData,
              feelMoreClear: value
            }))}
              {renderLikertScale('Interactions with my cofounder often leave me feeling frustrated or depleted.', formData.feelFrustratedDepleted, value => setFormData({
              ...formData,
              feelFrustratedDepleted: value
            }))}
            </div>

            <div className="mb-8" data-id="element-247">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-248">
                Social Integration
              </h4>
              {renderLikertScale('We spend time together socially outside of work.', formData.spendTimeSocially, value => setFormData({
              ...formData,
              spendTimeSocially: value
            }), ['Never', 'Rarely', 'Sometimes', 'Often', 'Very Often'])}

              <SingleSelectDropdown label="I have met my cofounder's close friends family or partner." options={['Yes — know them well', 'Yes — have met', 'Briefly', 'No']} selected={formData.metFriendsFamily} onChange={selected => setFormData({
              ...formData,
              metFriendsFamily: selected
            })} placeholder="Select answer..." data-id="element-249" />

              <SingleSelectDropdown label="We have had at least one honest conversation about personal values money or life ambitions beyond the company." options={['Yes — deep conversations', 'Yes — touched on it', 'Not really', 'No']} selected={formData.honestConversation} onChange={selected => setFormData({
              ...formData,
              honestConversation: selected
            })} placeholder="Select answer..." data-id="element-250" />
            </div>

            <div data-id="element-251">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-252">
                3+ Cofounders (if applicable)
              </h4>
              <p className="text-[#3D5654] mb-4 text-sm" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-253">
                Answer these questions only if you have 3 or more cofounders
              </p>

              <SingleSelectDropdown label="Do alliances or sides form within your cofounder group?" options={['Yes — often', 'Yes — occasionally', 'Rarely', 'Never', 'Not applicable (2 cofounders)']} selected={formData.alliancesForm} onChange={selected => setFormData({
              ...formData,
              alliancesForm: selected
            })} placeholder="Select answer..." data-id="element-254" />

              <SingleSelectDropdown label="Is there a clear leader or first among equals?" options={['Yes — one clear leader', 'It rotates', 'No — truly equal', "No — and it's unclear", 'Not applicable (2 cofounders)']} selected={formData.clearLeader} onChange={selected => setFormData({
              ...formData,
              clearLeader: selected
            })} placeholder="Select answer..." data-id="element-255" />

              {renderLikertScale('Decision-making is more complicated with 3+ cofounders.', formData.decisionMakingComplicated, value => setFormData({
              ...formData,
              decisionMakingComplicated: value
            }))}
              {renderLikertScale('Having 3+ cofounders provides more support and diverse perspectives.', formData.moreSupport, value => setFormData({
              ...formData,
              moreSupport: value
            }))}
              {renderLikertScale('Communication is harder to maintain with 3+ cofounders.', formData.communicationHarder, value => setFormData({
              ...formData,
              communicationHarder: value
            }))}

              <SingleSelectDropdown label="If you could go back would you choose to have the same number of cofounders?" options={['Yes', 'No — fewer would be better', 'No — more would be better', 'Unsure', 'Not applicable (2 cofounders)']} selected={formData.sameNumberOfCofounders} onChange={selected => setFormData({
              ...formData,
              sameNumberOfCofounders: selected
            })} placeholder="Select answer..." data-id="element-256" />
            </div>
          </div>;
      case 'behavioral':
        return <div className="space-y-6" data-id="element-257">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-258">
              Behavioral Indicators
            </h3>

            <SingleSelectDropdown label="In the past 30 days how many times have you and your cofounder had a dedicated check-in about your relationship (not just business)?" options={['0', '1–2', '3–4', '5+', 'Weekly or more']} selected={formData.relationshipCheckIns} onChange={selected => setFormData({
            ...formData,
            relationshipCheckIns: selected
          })} placeholder="Select frequency..." data-id="element-259" />

            <SingleSelectDropdown label="In the past 30 days have you given your cofounder constructive feedback about their performance or behavior?" options={['Yes — multiple times', 'Yes — once', 'No']} selected={formData.gaveConstructiveFeedback} onChange={selected => setFormData({
            ...formData,
            gaveConstructiveFeedback: selected
          })} placeholder="Select answer..." data-id="element-260" />

            <SingleSelectDropdown label="In the past 30 days has your cofounder given you constructive feedback?" options={['Yes — multiple times', 'Yes — once', 'No', 'Unsure']} selected={formData.receivedConstructiveFeedback} onChange={selected => setFormData({
            ...formData,
            receivedConstructiveFeedback: selected
          })} placeholder="Select answer..." data-id="element-261" />

            <SingleSelectDropdown label="In the past 90 days have you had a significant disagreement or conflict with your cofounder?" options={['Yes — several', 'Yes — one significant', 'Minor disagreements only', 'No conflict']} selected={formData.significantDisagreement} onChange={selected => setFormData({
            ...formData,
            significantDisagreement: selected
          })} placeholder="Select answer..." data-id="element-262" />

            <SingleSelectDropdown label="If yes how was it resolved?" options={['Fully resolved through discussion', 'Partially resolved', 'Avoided or swept under rug', 'Still unresolved', 'Not applicable']} selected={formData.howResolved} onChange={selected => setFormData({
            ...formData,
            howResolved: selected
          })} placeholder="Select resolution..." data-id="element-263" />

            <SingleSelectDropdown label="Do you have a formal written cofounder agreement?" options={['Yes — comprehensive', 'Yes — basic', 'In progress', 'No — but planning to', 'No']} selected={formData.formalAgreement} onChange={selected => setFormData({
            ...formData,
            formalAgreement: selected
          })} placeholder="Select answer..." data-id="element-264" />

            <SingleSelectDropdown label="Have you explicitly discussed what happens if one cofounder wants to leave?" options={['Yes — detailed agreement', 'Yes — general understanding', 'Briefly mentioned', 'No']} selected={formData.discussedLeaving} onChange={selected => setFormData({
            ...formData,
            discussedLeaving: selected
          })} placeholder="Select answer..." data-id="element-265" />
          </div>;
      case 'outcomes':
        return <div className="space-y-6" data-id="element-266">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-267">
              Outcomes
            </h3>

            <div className="mb-8" data-id="element-268">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-269">
                Outcome Ratings
              </h4>

              {render1to10Scale('On a scale of 1–10 how strong is your cofounder relationship today?', formData.relationshipStrength, value => setFormData({
              ...formData,
              relationshipStrength: value
            }), 'Extremely Weak', 'Extremely Strong')}
              {render1to10Scale('On a scale of 1–10 how likely is it that you and your cofounder will still be working together in 3 years?', formData.likelyWorkingIn3Years, value => setFormData({
              ...formData,
              likelyWorkingIn3Years: value
            }), 'Extremely Unlikely', 'Extremely Likely')}
              {render1to10Scale('On a scale of 1–10 how likely do you think your cofounder would give the same answer to the previous question?', formData.cofounderSameAnswer, value => setFormData({
              ...formData,
              cofounderSameAnswer: value
            }), 'Very Different Answer', 'Exact Same Answer')}
              {render1to10Scale('On a scale of 1–10 how satisfied are you with your cofounder partnership overall?', formData.satisfactionOverall, value => setFormData({
              ...formData,
              satisfactionOverall: value
            }), 'Extremely Dissatisfied', 'Extremely Satisfied')}
              {render1to10Scale('On a scale of 1–10 how confident are you that this partnership will survive a major crisis or setback?', formData.survivesCrisis, value => setFormData({
              ...formData,
              survivesCrisis: value
            }), 'Not At All Confident', 'Extremely Confident')}
              {render1to10Scale('On a scale of 1–10 how much does your cofounder relationship contribute to the success of your company?', formData.contributesToSuccess, value => setFormData({
              ...formData,
              contributesToSuccess: value
            }), 'Not At All', 'Critical Factor')}
            </div>

            <div data-id="element-270">
              <h4 className="text-lg font-medium text-[#1A3D3A] mb-4" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} data-id="element-271">
                Outcome Questions
              </h4>

              <SingleSelectDropdown label="Would you choose this cofounder again if starting over?" options={['Yes — without hesitation', 'Yes — with some changes', 'Unsure', 'Probably not', 'Definitely not']} selected={formData.chooseAgain} onChange={selected => setFormData({
              ...formData,
              chooseAgain: selected
            })} placeholder="Select answer..." data-id="element-272" />

              <SingleSelectDropdown label="Would you start another business with this cofounder in the future?" options={['Yes — definitely', 'Probably', 'Maybe', 'Probably not', 'Definitely not']} selected={formData.startAnotherBusiness} onChange={selected => setFormData({
              ...formData,
              startAnotherBusiness: selected
            })} placeholder="Select answer..." data-id="element-273" />

              <SingleSelectDropdown label="Have you ever seriously considered ending this cofounder partnership?" options={['Yes — currently considering', 'Yes — in the past', 'Briefly thought about it', 'Never']} selected={formData.consideredEnding} onChange={selected => setFormData({
              ...formData,
              consideredEnding: selected
            })} placeholder="Select answer..." data-id="element-274" />

              <SingleSelectDropdown label="Do you believe your cofounder relationship will last through a major exit or pivot?" options={['Yes — confident', 'Probably', 'Unsure', 'Probably not', 'No']} selected={formData.lastThroughExit} onChange={selected => setFormData({
              ...formData,
              lastThroughExit: selected
            })} placeholder="Select answer..." data-id="element-275" />
            </div>
          </div>;
      case 'experience':
        return <div className="space-y-6" data-id="element-276">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-277">
              Experience & History
            </h3>

            <SingleSelectDropdown label="Have you experienced a cofounder split or departure in any venture (past or present)?" options={['Yes — I left', 'Yes — cofounder left', 'Yes — in a different venture', 'No — never']} selected={formData.experiencedSplit} onChange={selected => setFormData({
            ...formData,
            experiencedSplit: selected
          })} placeholder="Select answer..." data-id="element-278" />

            <MultiSelectDropdown label="If yes what was the primary cause? (Select all that apply)" options={['Vision differences', 'Commitment differences', 'Performance issues', 'Equity disputes', 'Personal conflict', 'Life circumstances', 'Better opportunity', 'Burnout', 'Loss of trust', 'Investor pressure', 'Should never have partnered', 'Other']} selected={formData.splitCauses} onChange={selected => setFormData({
            ...formData,
            splitCauses: selected
          })} placeholder="Select causes..." data-id="element-279" />

            <SingleSelectDropdown label="Have you worked with a coach therapist advisor or mediator to support your cofounder relationship?" options={['Yes — currently', 'Yes — in the past', 'No — but would consider', 'No — not interested']} selected={formData.workedWithCoach} onChange={selected => setFormData({
            ...formData,
            workedWithCoach: selected
          })} placeholder="Select answer..." data-id="element-280" />
          </div>;
      case 'reflections':
        return <div className="space-y-6" data-id="element-281">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-282">
              Reflections
            </h3>

            <div data-id="element-283">
              <label className="block text-[#1A3D3A] mb-2" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              fontWeight: 500
            }} data-id="element-284">
                What is one thing you most value or admire about your cofounder?
              </label>
              <input type="text" value={formData.mostValue} onChange={e => setFormData({
              ...formData,
              mostValue: e.target.value
            })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="What you value most..." data-id="element-285" />
            </div>

            <div data-id="element-286">
              <label className="block text-[#1A3D3A] mb-2" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              fontWeight: 500
            }} data-id="element-287">
                What is one thing you wish you could change about your
                partnership?
              </label>
              <input type="text" value={formData.wishToChange} onChange={e => setFormData({
              ...formData,
              wishToChange: e.target.value
            })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="What you'd change..." data-id="element-288" />
            </div>

            <div data-id="element-289">
              <label className="block text-[#1A3D3A] mb-2" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              fontWeight: 500
            }} data-id="element-290">
                Describe a moment that tested your cofounder relationship. What
                happened and what did you learn?
              </label>
              <textarea value={formData.momentTested} onChange={e => setFormData({
              ...formData,
              momentTested: e.target.value
            })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} rows={4} placeholder="Describe the moment..." data-id="element-291" />
            </div>

            <div data-id="element-292">
              <label className="block text-[#1A3D3A] mb-2" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              fontWeight: 500
            }} data-id="element-293">
                Describe a moment when you felt most grateful for having a
                cofounder.
              </label>
              <textarea value={formData.momentGrateful} onChange={e => setFormData({
              ...formData,
              momentGrateful: e.target.value
            })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} rows={4} placeholder="Describe the moment..." data-id="element-294" />
            </div>

            <div data-id="element-295">
              <label className="block text-[#1A3D3A] mb-2" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              fontWeight: 500
            }} data-id="element-296">
                What do you know now about cofounder relationships that you wish
                you knew at the start?
              </label>
              <textarea value={formData.knowNow} onChange={e => setFormData({
              ...formData,
              knowNow: e.target.value
            })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} rows={4} placeholder="What you know now..." data-id="element-297" />
            </div>

            <div data-id="element-298">
              <label className="block text-[#1A3D3A] mb-2" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              fontWeight: 500
            }} data-id="element-299">
                What advice would you give someone about to start a company with
                a cofounder?
              </label>
              <textarea value={formData.adviceToOthers} onChange={e => setFormData({
              ...formData,
              adviceToOthers: e.target.value
            })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} rows={4} placeholder="Your advice..." data-id="element-300" />
            </div>

            <div data-id="element-301">
              <label className="block text-[#1A3D3A] mb-2" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              fontWeight: 500
            }} data-id="element-302">
                If you could tell your cofounder one thing you've never said
                what would it be?
              </label>
              <input type="text" value={formData.neverSaid} onChange={e => setFormData({
              ...formData,
              neverSaid: e.target.value
            })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="What you'd say..." data-id="element-303" />
            </div>

            <div data-id="element-304">
              <label className="block text-[#1A3D3A] mb-2" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              fontWeight: 500
            }} data-id="element-305">
                Why do you believe cofounder relationships ultimately succeed or
                fail?
              </label>
              <textarea value={formData.whySucceedOrFail} onChange={e => setFormData({
              ...formData,
              whySucceedOrFail: e.target.value
            })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} rows={4} placeholder="Your thoughts..." data-id="element-306" />
            </div>

            <div data-id="element-307">
              <label className="block text-[#1A3D3A] mb-2" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              fontWeight: 500
            }} data-id="element-308">
                Is there anything else you'd like to share about your cofounder
                experience?
              </label>
              <textarea value={formData.anythingElse} onChange={e => setFormData({
              ...formData,
              anythingElse: e.target.value
            })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} rows={4} placeholder="Additional thoughts..." data-id="element-309" />
            </div>

            <div data-id="element-310">
              <label className="block text-[#1A3D3A] mb-2" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              fontWeight: 500
            }} data-id="element-311">
                Looking back is there anything you would do differently about
                how equity was discussed or divided?
              </label>
              <input type="text" value={formData.equityDoDifferently} onChange={e => setFormData({
              ...formData,
              equityDoDifferently: e.target.value
            })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="What you'd do differently..." data-id="element-312" />
            </div>

            <div data-id="element-313">
              <label className="block text-[#1A3D3A] mb-2" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              fontWeight: 500
            }} data-id="element-314">
                What advice would you give other cofounders about handling
                equity conversations?
              </label>
              <input type="text" value={formData.equityAdvice} onChange={e => setFormData({
              ...formData,
              equityAdvice: e.target.value
            })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} placeholder="Your equity advice..." data-id="element-315" />
            </div>

            <div data-id="element-316">
              <label className="block text-[#1A3D3A] mb-2" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              fontWeight: 500
            }} data-id="element-317">
                During the hardest moment of your startup journey how did your
                cofounder show up? What did that mean to you?
              </label>
              <textarea value={formData.crisisShowUp} onChange={e => setFormData({
              ...formData,
              crisisShowUp: e.target.value
            })} className="w-full px-4 py-3 border-2 border-[#1A3D3A]/20 rounded-lg focus:border-[#1A3D3A] focus:outline-none" style={{
              fontFamily: "'DM Sans', sans-serif"
            }} rows={4} placeholder="How they showed up..." data-id="element-318" />
            </div>
          </div>;
      case 'follow-up':
        return <div className="space-y-6" data-id="element-319">
            <h3 className="text-xl font-medium text-[#1A3D3A] border-b border-[#1A3D3A]/10 pb-2 mb-6" style={{
            fontFamily: "'Cormorant Garamond', serif"
          }} data-id="element-320">
              Follow-up & Permissions
            </h3>

            <SingleSelectDropdown label="Would you be willing to take this survey again in 6 months so we can track changes?" options={['Yes', 'No', 'Maybe']} selected={formData.takeAgainIn6Months} onChange={selected => setFormData({
            ...formData,
            takeAgainIn6Months: selected
          })} placeholder="Select answer..." data-id="element-321" />

            <SingleSelectDropdown label="Would your cofounder be willing to complete this same survey?" options={["Yes — they've agreed", 'Probably', 'Unsure', 'Probably not', 'No']} selected={formData.cofounderWilling} onChange={selected => setFormData({
            ...formData,
            cofounderWilling: selected
          })} placeholder="Select answer..." data-id="element-322" />

            <SingleSelectDropdown label="Would you be open to a follow-up interview for potential inclusion in a book?" options={['Yes', 'No', 'Maybe']} selected={formData.openToInterview} onChange={selected => setFormData({
            ...formData,
            openToInterview: selected
          })} placeholder="Select answer..." data-id="element-323" />

            <SingleSelectDropdown label="May we quote you in future publications or research?" options={['Yes — with my name', 'Yes — anonymously only', 'No']} selected={formData.mayQuote} onChange={selected => setFormData({
            ...formData,
            mayQuote: selected
          })} placeholder="Select answer..." data-id="element-324" />

            <div className="mt-8 p-6 bg-[#C5F467]/20 rounded-lg" data-id="element-325">
              <p className="text-[#1A3D3A]" style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: '15px',
              lineHeight: 1.6
            }} data-id="element-326">
                Thank you for completing this survey! Your responses will
                contribute to research helping cofounders build stronger
                partnerships.
              </p>
            </div>
          </div>;
      default:
        return <div data-id="element-327">Section not found</div>;
    }
  };
  return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 overflow-y-auto" data-id="element-328">
      <div className="min-h-screen flex items-start justify-center p-4 py-8" data-id="element-329">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-[16px] w-full max-w-4xl relative" data-id="element-330">
          <div className="p-6 border-b border-[#1A3D3A]/10" data-id="element-331">
            <div className="flex justify-between items-start mb-4" data-id="element-332">
              <div data-id="element-333">
                <h2 className="text-[#1A3D3A] mb-2" style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: '28px',
                fontWeight: 500
              }} data-id="element-334">
                  Cofounder Relationship Survey
                </h2>
                <p className="text-[#3D5654] text-sm" style={{
                fontFamily: "'DM Sans', sans-serif"
              }} data-id="element-335">
                  {completedSections.size} of {SECTIONS.length} sections
                  completed
                </p>
              </div>
              <button onClick={onClose} className="text-[#3D5654] hover:text-[#1A3D3A] transition-colors" data-id="element-336">
                <X className="w-6 h-6" data-id="element-337" />
              </button>
            </div>

            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" data-id="element-338">
              {SECTIONS.map(section => <button key={section.id} onClick={() => handleSectionChange(section.id)} className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm transition-all flex items-center gap-2 ${currentSection === section.id ? 'bg-[#1A3D3A] text-white' : completedSections.has(section.id) ? 'bg-[#C5F467] text-[#1A3D3A]' : 'bg-[#F8FAF9] text-[#3D5654] hover:bg-[#E8EAE9]'}`} style={{
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 500
            }} data-id="element-339">
                  {completedSections.has(section.id) && <Check className="w-4 h-4" data-id="element-340" />}
                  {section.label}
                </button>)}
            </div>
          </div>

          <div className="p-8" data-id="element-341">
            <AnimatePresence mode="wait" data-id="element-342">
              <motion.div key={currentSection} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.2
            }} data-id="element-343">
                {renderSection()}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="p-6 border-t border-[#1A3D3A]/10 flex justify-between items-center" data-id="element-344">
            <div className="text-sm text-[#3D5654]" style={{
            fontFamily: "'DM Sans', sans-serif"
          }} data-id="element-345">
              {isSaving && <span className="text-[#1A3D3A]" data-id="element-346">💾 Saving...</span>}
            </div>
            <button onClick={currentSection === 'follow-up' ? handleSubmit : goToNextSection} disabled={isSubmitting || isSaving} className="flex items-center gap-2 px-8 py-3 bg-[#1A3D3A] text-white rounded-lg hover:opacity-90 transition-all disabled:opacity-50" style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: '14px',
            fontWeight: 600
          }} data-id="element-347">
              {isSubmitting ? 'Submitting...' : isSaving ? 'Saving...' : currentSection === 'follow-up' ? 'Submit Survey' : <>
                  Continue
                  <ChevronRight className="w-4 h-4" data-id="element-348" />
                </>}
            </button>
          </div>
        </motion.div>
      </div>
    </div>;
}